import {
  DocumentMapping,
  DynamicDocumentInstructions,
  MARK_HIGH_PRIORITY_WORKFLOW,
} from "@/configuration/document-mappings";
import heartplaceInsuranceMappingData from "@/configuration/groups/heartplace/insurance-mapping.json";
import { readHpCustomerData } from "@/configuration/groups/heartplace/workflows/preflight-customer-data";
import { AppointmentStatus } from "@/packages/shared/src/models/appointment";
import {
  AutoIntakeEligibilityRequirement,
  type AutoIntakeEligibilityRequirements,
} from "@/src/lib/auto-intake/auto-intake-eligibility-requirements";
import { autoIntakeRule, type AutoIntakeRule } from "@/src/lib/auto-intake/auto-intake-rule";
import { DocRoutingStrategy, ReferralRoutingStrategy } from "@/src/lib/constants";
import {
  buildPatientMetadataInsuranceVariables,
  resolveCoverageEcwId,
  type EcwInsuranceMapping,
  type EcwInsuranceMappingEntry,
  type PatientMetadataInsuranceVariables,
} from "@/src/lib/ecw-insurance-mapping";
import { EncounterStatus, EncounterType } from "@/src/lib/ehr/customer/encounter-filters";
import type { EcwEncounterExclusions } from "@/src/lib/ehr/ecw/ecw-webecw-helpers";
import { logAndThrow, logger } from "@/src/lib/logging/logger";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { isUuidSelected, uuidToRandom } from "@/src/lib/uuid";
import { type EcwCredentials } from "@/src/lib/web-automation/cloudcruise/lab-di";
import type { StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { AppointmentType } from "@/src/models/appointment";
import { Classification, ClassificationCategory, ClassificationSection } from "@/src/models/constants/classifications";
import { Workflow, WorkflowType } from "@/src/models/constants/group";
import { ImagingType } from "@/src/models/constants/imaging-types";
import { Coverage } from "@/src/models/coverage";
import { MedicalRecordRequest } from "@/src/models/documents/medical-records-request/medical-record-request";
import { isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { Group } from "@/src/models/group";
import { isPatientNotInEhr, Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";
import { Section } from "@/src/models/section";
import { Task, TaskStatus, TaskType } from "@/src/models/task";

export const HEARTPLACE_REGION_FORT_WORTH = "fort_worth" as const;
export const HEARTPLACE_REGION_HEARTPLACE = "heartplace" as const;
export const HEARTPLACE_PROVIDER_STAFF_ASSIGNMENT = "Provider,staff" as const;
export const HEARD_STAFF_ASSIGNMENT = "Heard,Staff" as const;

export const HEARTPLACE_PAGE_RANGES_RULE =
  "Only create separate pageRanges when you detect DIFFERENT PATIENTS (different names). " +
  "For documents with the same patient, combine all pages into a single pageRange regardless of visit date. " +
  "When comparing patient names, ignore capitalization differences (e.g., 'JOHN DOE' and 'John Doe' are the same patient). " +
  "Treat names with minor typos or small spelling variations as the same patient (e.g., 'Jon Smith' and 'John Smith' should be grouped together). " +
  "If a page has no patient name, group it with adjacent pages that DO have a name based on context clues like formatting, date proximity, or document structure.";

export const HEARTPLACE_EP_PROVIDER_NPI = [
  "1295921989",
  "1164530739",
  "1962611830",
  "1548479827",
  "1033300074",
  "1316332638",
  "1386664852",
  "1922426204",
];

const PLANO_WEST_FAX_NUMBER = "+18442904363";

export const FRISCO_FAX_NUMBER = "+18333621209";
const ALLEN_FAX_NUMBER = "+13133865054";
const ALLEN_2_FAX_NUMBER = "+18442904360";
const HEB_FAX_NUMBER = "+18442904362";
const PLANO_FAX_NUMBER = "+18442921457";
const RENNER_FAX_NUMBER = "+18442897691";
const NORTH_ARLINGTON_FAX_NUMBER = "+18442904365";
const SOUTH_ARLINGTON_FAX_NUMBER = "+18442921463";
const BAYLOR_FAX_NUMBER = "+18442921458";
const BAYLOR_PLANO_FAX_NUMBER = "+18442921461";
const NORTH_HILLS_FAX_NUMBER = "+18442921464";
const BEDFORD_IMAGING_FAX_NUMBER = "+18339723547";
const GARLAND_FAX_NUMBER = "+18442921462";
const CORSICANA_FAX_NUMBER = "+18442904361";
const MESQUITE_FAX_NUMBER = "+18442904367";
const RED_OAK_FAX_NUMBER = "+18442921456";
const PLANO_IMAGING_FAX_NUMBER = "+18334493854";
const MANSFIELD_FAX_NUMBER = "+18442921460";
const MEDICAL_CITY_FAX_NUMBER = "+18442904358";
const GREENVILLE_FAX_NUMBER = "+18338750109";
const MCKINNEY_FAX_NUMBER = "+18449717071";
const SOUTHLAKE_FAX_NUMBER = "+18442897683";

export const ICC_FAX_NUMBER = "+18339733706";
const CAMPBELL_FAX_NUMBER = "+18442921459";
const MID_CITIES_FAX_NUMBER = "+18442897694";
const HUGULEY_FAX_NUMBER = "+18442904359";
const GLEN_ROSE_FAX_NUMBER = "+12548971409";

export const MIDLOTHIAN_FAX_NUMBER = "+18339725635";
const LAS_COLINAS_FAX_NUMBER = "+18339441908";
const MISTLETOE_FAX_NUMBER = "+18662686998";
const GRANBURY_FAX_NUMBER = "+18662687174";
const MIRA_VISTA_FAX_NUMBER = "+18662687175";
const FORT_WORTH_NORTH_ARLINGTON_FAX_NUMBER = "+18173359871";

const ALLEN_WESTFAX_PRODUCT_ID = "5ebf4aad-bf0d-4c1c-8855-2bc7b4fe1eae";
const BAYLOR_WESTFAX_PRODUCT_ID = "9373fd08-b41b-41c3-a281-f1925d928a68";
const BAYLOR_PLANO_WESTFAX_PRODUCT_ID = "22f0e557-d004-470d-8541-e36460b21093";
const BEDFORD_IMAGING_CLINIC_WESTFAX_PRODUCT_ID = "9af4ce48-d3c3-46a2-92a1-d0448a42703d";
const CORSICANA_WESTFAX_PRODUCT_ID = "7d4446e2-7a40-415f-9286-c6a05fc0c0cd";
const FRISCO_WESTFAX_PRODUCT_ID = "006fc71a-1513-4a27-9d87-4b22b0021f8c";
const GARLAND_WESTFAX_PRODUCT_ID = "d7bbcebd-c9fd-42f9-b4d7-e5b3037d8943";
const GREENVILLE_WESTFAX_PRODUCT_ID = "0450de6f-9249-4ae4-908e-7259d7ac2391";
const HEB_WESTFAX_PRODUCT_ID = "34ad23f7-25cd-4f0f-aa00-33d2f164e90f";
const HUGULEY_WESTFAX_PRODUCT_ID = "561a9361-eb08-4e51-9a2c-45d3b01733a6";
const INTEGRATED_CARE_CLINIC_WESTFAX_PRODUCT_ID = "f19f056f-2f4b-469f-9b2d-d70730e74fc6";
const LAS_COLINAS_WESTFAX_PRODUCT_ID = "a852cfa4-15af-404d-ae65-f54291e8a536";
const MANSFIELD_WESTFAX_PRODUCT_ID = "e3b264f0-e562-40db-8b48-98b776be6c2e";
const MCKINNEY_WESTFAX_PRODUCT_ID = "5b0d2c6a-ee9f-403f-94b2-dc1570ac9db4";
const MEDICAL_CITY_WESTFAX_PRODUCT_ID = "20d9976d-c833-4497-9236-515f07aaf6e5";
const MESQUITE_WESTFAX_PRODUCT_ID = "481a125f-0e1b-4723-a2d7-feca558d8e4f";
const MID_CITIES_WESTFAX_PRODUCT_ID = "46d9ad81-ec8b-413c-9e2d-182e1d80bb7a";
const MIDLOTHIAN_WESTFAX_PRODUCT_ID = "8e5ee5f7-c40c-4bab-885f-c156ba83c0fd";
const NORTH_ARLINGTON_WESTFAX_PRODUCT_ID = "335264a5-543b-4a22-aca4-b7467d12a36e";
const NORTH_HILLS_WESTFAX_PRODUCT_ID = "04055e75-07aa-4089-977b-6c319abf8165";
const PLANO_WESTFAX_PRODUCT_ID = "eca1debf-c154-4358-a504-4657ac08d9d7";
const PLANO_15_WESTFAX_PRODUCT_ID = "8e12f030-0014-4ea7-ab0e-000b71732f1c";
const PLANO_IMAGING_CLINIC_WESTFAX_PRODUCT_ID = "48633768-450a-4225-b192-b002f87537e9";
const RED_OAK_WESTFAX_PRODUCT_ID = "904bba4d-3261-429c-bd33-81e45cc22b97";
const RENNER_WESTFAX_PRODUCT_ID = "c431e9d7-c990-4e87-8292-80fb00d25590";
const RICHARDSON_CAMPBELL_WESTFAX_PRODUCT_ID = "70cb7347-3b17-4e0e-b69e-f90f3f9f4e57";
const SOUTH_ARLINGTON_WESTFAX_PRODUCT_ID = "f42f5516-fab0-491e-b9ac-2896fd18e5f2";
const SOUTHLAKE_WESTFAX_PRODUCT_ID = "3363aa8a-2782-4ffa-b354-3d4f52279905";
const MISTLETOE_WESTFAX_PRODUCT_ID = "61390b28-aff8-4383-be58-536aa10607aa";
const GRANBURY_WESTFAX_PRODUCT_ID = "effc10b5-008c-424d-9470-c37258fa2c4a";
const MIRA_VISTA_WESTFAX_PRODUCT_ID = "9927b264-2e7b-4c86-8fff-e1832d15acb1";
const FORT_WORTH_NORTH_ARLINGTON_WESTFAX_PRODUCT_ID = "715dd6fe-7d1a-40b1-8925-6d0eeb9fcf96";

export const HEARTPLACE_WESTFAX_PRODUCT_IDS = [
  ALLEN_WESTFAX_PRODUCT_ID,
  BAYLOR_WESTFAX_PRODUCT_ID,
  BAYLOR_PLANO_WESTFAX_PRODUCT_ID,
  BEDFORD_IMAGING_CLINIC_WESTFAX_PRODUCT_ID,
  CORSICANA_WESTFAX_PRODUCT_ID,
  FRISCO_WESTFAX_PRODUCT_ID,
  GARLAND_WESTFAX_PRODUCT_ID,
  GREENVILLE_WESTFAX_PRODUCT_ID,
  HEB_WESTFAX_PRODUCT_ID,
  HUGULEY_WESTFAX_PRODUCT_ID,
  INTEGRATED_CARE_CLINIC_WESTFAX_PRODUCT_ID,
  LAS_COLINAS_WESTFAX_PRODUCT_ID,
  MANSFIELD_WESTFAX_PRODUCT_ID,
  MCKINNEY_WESTFAX_PRODUCT_ID,
  MEDICAL_CITY_WESTFAX_PRODUCT_ID,
  MESQUITE_WESTFAX_PRODUCT_ID,
  MID_CITIES_WESTFAX_PRODUCT_ID,
  MIDLOTHIAN_WESTFAX_PRODUCT_ID,
  NORTH_ARLINGTON_WESTFAX_PRODUCT_ID,
  NORTH_HILLS_WESTFAX_PRODUCT_ID,
  PLANO_WESTFAX_PRODUCT_ID,
  PLANO_15_WESTFAX_PRODUCT_ID,
  PLANO_IMAGING_CLINIC_WESTFAX_PRODUCT_ID,
  RED_OAK_WESTFAX_PRODUCT_ID,
  RENNER_WESTFAX_PRODUCT_ID,
  RICHARDSON_CAMPBELL_WESTFAX_PRODUCT_ID,
  SOUTH_ARLINGTON_WESTFAX_PRODUCT_ID,
  SOUTHLAKE_WESTFAX_PRODUCT_ID,
  MISTLETOE_WESTFAX_PRODUCT_ID,
  GRANBURY_WESTFAX_PRODUCT_ID,
  MIRA_VISTA_WESTFAX_PRODUCT_ID,
  FORT_WORTH_NORTH_ARLINGTON_WESTFAX_PRODUCT_ID,
] as const;

/** WestFax lines for Heartplace-area clinics used for scheduling region resolution. */
export const HEARTPLACE_FAX_NUMBERS = [
  PLANO_FAX_NUMBER,
  PLANO_WEST_FAX_NUMBER,
  SOUTH_ARLINGTON_FAX_NUMBER,
  FRISCO_FAX_NUMBER,
  ALLEN_FAX_NUMBER,
  ALLEN_2_FAX_NUMBER,
  GARLAND_FAX_NUMBER,
  NORTH_ARLINGTON_FAX_NUMBER,
  MANSFIELD_FAX_NUMBER,
  BAYLOR_FAX_NUMBER,
  BAYLOR_PLANO_FAX_NUMBER,
  NORTH_HILLS_FAX_NUMBER,
  HEB_FAX_NUMBER,
  SOUTHLAKE_FAX_NUMBER,
  HUGULEY_FAX_NUMBER,
  CAMPBELL_FAX_NUMBER,
  MEDICAL_CITY_FAX_NUMBER,
  RED_OAK_FAX_NUMBER,
  CORSICANA_FAX_NUMBER,
  GREENVILLE_FAX_NUMBER,
  MESQUITE_FAX_NUMBER,
  RENNER_FAX_NUMBER,
  LAS_COLINAS_FAX_NUMBER,
  MCKINNEY_FAX_NUMBER,
] as const as string[];

/** WestFax lines for Fort Worth clinics ingested on the Heartplace live group. */
export const HEARTPLACE_FORT_WORTH_FAX_NUMBERS = [
  MISTLETOE_FAX_NUMBER,
  GRANBURY_FAX_NUMBER,
  MIRA_VISTA_FAX_NUMBER,
  FORT_WORTH_NORTH_ARLINGTON_FAX_NUMBER,
  GLEN_ROSE_FAX_NUMBER,
] as const as string[];

export const HEARTPLACE_SCHEDULING_FAX_NUMBERS = [...HEARTPLACE_FAX_NUMBERS, ...HEARTPLACE_FORT_WORTH_FAX_NUMBERS];

export const HEARTPLACE_MCKINNEY_LOCATION_EHR_ID = "174";

export function isHeartplaceFortWorthFaxNumber(faxRecipientNumber: string | null): boolean {
  if (!faxRecipientNumber) return false;

  return HEARTPLACE_FORT_WORTH_FAX_NUMBERS.includes(faxRecipientNumber);
}

/**
 * Returns true when the receiving location on a Heartplace referral is the McKinney clinic.
 * Checks the EHR ID first (reliable when the location has been matched), then falls back to
 * a case-insensitive name match for newly-extracted referrals where ehrId may not yet be set.
 */
export function isHeartplaceMcKinneyReceivingLocation(
  receivingLocation: { ehrId?: string | null; name?: string | null } | null
): boolean {
  if (!receivingLocation) return false;
  if (receivingLocation.ehrId === HEARTPLACE_MCKINNEY_LOCATION_EHR_ID) return true;

  return receivingLocation.name?.toLowerCase().includes("mckinney") ?? false;
}

export function getHeartplaceReferralReasonText(referral: Referral, isFortWorth: boolean): string {
  const hasInsurance = !!(referral.primaryCoverage || referral.secondaryCoverage);
  let base = "Cardiology referral";

  if (isFortWorth) {
    const reason = referral.reasonForReferral?.trim() || referral.diagnosisList?.[0]?.diagnosisText?.trim() || "";

    if (reason) base = `Cardiology referral - ${reason}`;
  }

  return hasInsurance ? base : `${base}. Referral documents missing insurance.`;
}

export const HEARTPLACE_SCHEDULING_TAG = "scheduling needed";

export const HEARTPLACE_SCHEDULING_FAX_TO_ASSIGNMENT: Record<string, string> = {
  ...Object.fromEntries(HEARTPLACE_SCHEDULING_FAX_NUMBERS.map((fax) => [fax, "Webb,Ian"])),
  [SOUTH_ARLINGTON_FAX_NUMBER]: "Webb,Ian",
};

export const HEARTPLACE_REFERRAL_FAX_TO_ASSIGNMENT: Record<string, string> = {
  [MEDICAL_CITY_FAX_NUMBER]: "HP Medical City, Referrals",
  [BAYLOR_FAX_NUMBER]: "HP Baylor, Referrals",
};

export type HeartplaceInsuranceMappingEntry = EcwInsuranceMappingEntry;
export type HeartplaceInsuranceMapping = EcwInsuranceMapping;

export const HEARTPLACE_INSURANCE_MAPPING: HeartplaceInsuranceMapping =
  heartplaceInsuranceMappingData as HeartplaceInsuranceMapping;

export function resolveHeartplaceCoverageEcwId(coverage: Coverage | null, forPrimary: boolean): string | null {
  return resolveCoverageEcwId(coverage, HEARTPLACE_INSURANCE_MAPPING, forPrimary);
}

export type HeartplacePatientMetadataInsuranceVariables = PatientMetadataInsuranceVariables;

export function getHeartplacePatientMetadataInsuranceVariables(
  section: Section
): HeartplacePatientMetadataInsuranceVariables {
  const referral = section.reviewedContent as Referral | null;
  const primary = referral?.primaryCoverage ?? null;
  const secondary = referral?.secondaryCoverage ?? null;
  const primaryEcwId = resolveHeartplaceCoverageEcwId(primary, true);
  const secondaryEcwId = resolveHeartplaceCoverageEcwId(secondary, false);

  return buildPatientMetadataInsuranceVariables(primary, secondary, primaryEcwId, secondaryEcwId);
}

export const classificationToHeartplaceMap = {
  [ClassificationCategory.ADMIN]: [
    Classification.ADMIN,
    Classification.ADMIN_CERTIFICATE_OF_MEDICAL_NECESSITY,
    Classification.ADMIN_DEATH_CERTIFICATES,
    Classification.ADMIN_DISABILITY_FORMS,
    Classification.ADMIN_FMLA_FORMS,
    Classification.ADMIN_PATIENT_QUESTIONNAIRE,
    Classification.ADMIN_TRANSITION_CARE,
  ],
  [ClassificationCategory.CLEARANCE_REQUEST]: [Classification.CLEARANCE_REQUEST],
  [ClassificationCategory.DME_ORDER]: [Classification.DME_ORDER],
  [ClassificationCategory.IDENTIFICATION_CARD]: [Classification.IDENTIFICATION_CARD],
  [ClassificationCategory.IMAGING_RESULTS]: [
    Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING,
    Classification.IMAGING_RESULTS_COLONOSCOPY,
    Classification.IMAGING_RESULTS_CT,
    Classification.IMAGING_RESULTS_ECHO,
    Classification.IMAGING_RESULTS_EKG,
    Classification.IMAGING_RESULTS_ENDOSCOPY,
    Classification.IMAGING_RESULTS_HOLTER_MONITOR,
    Classification.IMAGING_RESULTS_MAMMOGRAM,
    Classification.IMAGING_RESULTS_MONITOR_NOTIFICATION,
    Classification.IMAGING_RESULTS_MRI,
    Classification.IMAGING_RESULTS_NM,
    Classification.IMAGING_RESULTS_PET,
    Classification.IMAGING_RESULTS_STRESS_TEST_EXERCISE,
    Classification.IMAGING_RESULTS_ULTRASOUND,
    Classification.IMAGING_RESULTS_X_RAY,
  ],
  [ClassificationCategory.INSURANCE_CARD]: [Classification.INSURANCE_CARD],
  [ClassificationCategory.INTAKE]: [Classification.INTAKE],
  [ClassificationCategory.LAB_RESULTS]: [
    Classification.LAB_RESULTS,
    Classification.LAB_RESULTS_BP,
    Classification.LAB_RESULTS_PATHOLOGY,
    Classification.LAB_RESULTS_PT_INR,
    Classification.LAB_RESULTS_QUANTAFLO,
  ],
  [ClassificationCategory.LEGAL]: [Classification.LEGAL],
  [ClassificationCategory.MEDICAL_HISTORY]: [
    Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC,
    Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL,
    Classification.MEDICAL_HISTORY_HOME_HEALTH,
    Classification.MEDICAL_HISTORY_MEDICATIONS,
  ],
  [ClassificationCategory.MEDICAL_RECORD_REQUEST]: [Classification.MEDICAL_RECORD_REQUEST],
  [ClassificationCategory.NOTIFICATION]: [Classification.NOTIFICATION_HOSPITAL_DISCHARGE],
  [ClassificationCategory.OTHER]: [Classification.OTHER],
  [ClassificationCategory.PHARMACY_RELATED]: [
    Classification.PHARMACY_RELATED_CARE_CONSIDERATION,
    Classification.PHARMACY_RELATED_CLARIFICATION,
    Classification.PHARMACY_RELATED_REFILL,
    Classification.PHARMACY_RELATED_ALERT,
  ],
  [ClassificationCategory.PRIOR_AUTHORIZATION]: [
    Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
  ],
  [ClassificationCategory.PROCEDURE]: [
    Classification.ORDER_CARDIAC,
    Classification.ORDER_CLARIFICATION,
    Classification.ORDER_IMAGING,
    Classification.ORDER_LAB,
    Classification.PROCEDURE_CARDIAC,
    Classification.PROCEDURE_CARDIAC_VEIN,
    Classification.PROCEDURE_DEVICE_INFO,
    Classification.PROCEDURE_DEVICE_REQUEST,
    Classification.PROCEDURE_REHAB,
    Classification.PROCEDURE_SLEEP,
  ],
  [ClassificationCategory.PROGRESS_NOTE]: [
    Classification.PROGRESS_NOTE,
    Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
    Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
    Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY,
    Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
  ],
  [ClassificationCategory.REFERRAL]: [Classification.REFERRAL, Classification.REFERRAL_RESPONSE],
  [ClassificationCategory.FINANCIAL]: [Classification.FINANCIAL_BILLING_RECORDS],
  [ClassificationCategory.JUNK]: [Classification.JUNK],
};

/**
 * Returns the classifications to display for selection for the Heartplace group
 *
 * @returns The classifications to display for selection for the Heartplace group
 */
export function getHeartplaceClassificationsToDisplayForSelection(): ClassificationSection[] {
  const classificationMap = Object.entries(classificationToHeartplaceMap).map(([label, options]) => {
    return {
      label,
      options: options.map((option) => option.toLowerCase()),
    };
  });

  return classificationMap;
}

/**
 * Returns the classifications for AI classification, excluding ADMIN and OTHER
 *
 * @returns The classifications for AI classification
 */
export function getHeartplaceClassificationsForAiClassification(): ClassificationSection[] {
  const classificationMap = Object.entries(classificationToHeartplaceMap)
    .map(([label, options]) => {
      return {
        label,
        options: options
          .filter((option) => option !== Classification.ADMIN && option !== Classification.OTHER)
          .map((option) => option.toLowerCase()),
      };
    })
    .filter((section) => section.options.length > 0); // Remove empty sections

  return classificationMap;
}

/**
 * Returns the workflows for the Heartplace group
 *
 * @param group - The group to get the workflows for
 * @returns The workflows for the Heartplace group
 */
export async function getHeartplaceWorkflows(_group: Group): Promise<Partial<Record<WorkflowType, Classification[]>>> {
  return {
    [Workflow.REFERRAL]: [Classification.REFERRAL],
    [Workflow.PRIOR_AUTHORIZATION]: [Classification.PRIOR_AUTHORIZATION],
    [Workflow.DOC_MANAGEMENT]: getHeartplaceClassificationForDocumentUpload(),
  };
}

/**
 * Returns the classifications to extract for the Heartplace group
 *
 * @returns The classifications to extract for the Heartplace group
 */
export function getHeartplaceClassificationToExtract(): Classification[] {
  const { [ClassificationCategory.JUNK]: _junk, ...classificationsWithoutJunk } = classificationToHeartplaceMap;

  return Object.values(classificationsWithoutJunk).flat();
}

/**
 * Returns the classifications for document upload for the Heartplace group
 *
 * @returns The classifications for document upload for the Heartplace group
 */
export function getHeartplaceClassificationForDocumentUpload(): Classification[] {
  return Object.values(classificationToHeartplaceMap).flat();
}

/**
 * Returns the holidays for the Heartplace group
 *
 * @returns The holidays for the Heartplace group
 */
export function getHeartplaceHolidays(): HolidayNames[] {
  return [
    HolidayNames.NEW_YEARS_DAY,
    HolidayNames.MEMORIAL_DAY,
    HolidayNames.INDEPENDENCE_DAY,
    HolidayNames.THANKSGIVING,
    HolidayNames.CHRISTMAS_DAY,
  ];
}

/**
 * Returns the document management instructions for the Heartplace group.
 * @param baseMapping - The base document mapping.
 * @param task - The task.
 * @param section - The section.
 * @param faxRecipientNumber - The fax recipient number.
 * @param stepOutputs - Step outputs used to build the provider hierarchy note.
 * @returns The document management instructions for the Heartplace group.
 */
export async function getHeartplaceDocumentManagementInstructions(
  baseMapping: DocumentMapping,
  task: Task,
  section: Section,
  faxRecipientNumber: string | null = null,
  stepOutputs?: StepOutputs
): Promise<DynamicDocumentInstructions> {
  const classification = section.uploadClassification || section.classification;
  const reviewedContent = section.reviewedContent || {};
  const note = readHpCustomerData(stepOutputs?.preflight?.customerData)?.providerHierarchyNote ?? null;
  const providerAssignment = stepOutputs?.preflight?.providerAssignment ?? null;
  const base: DynamicDocumentInstructions = { ...baseMapping };

  if (
    classification === Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST ||
    classification === Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE ||
    classification === Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE
  ) {
    if (isHeartplaceFortWorthFaxNumber(faxRecipientNumber)) {
      return { ...base, assignedTo: "!Authorization,CBO" };
    }

    return { ...base, assignedTo: "HP Auth, {location}" };
  }

  if (isHeartplaceFortWorthFaxNumber(faxRecipientNumber)) {
    if (classification === Classification.ADMIN_PATIENT_QUESTIONNAIRE) {
      return { ...base, assignedTo: "FWH {location}, Referrals" };
    }

    if (classification === Classification.REFERRAL) {
      const referral = reviewedContent as Referral;

      if (referral.authorizationNumber) {
        return { ...base, assignedTo: "FWH {location}, Referrals" };
      }
    }

    if (classification === Classification.MEDICAL_RECORD_REQUEST) {
      const priority = (reviewedContent as MedicalRecordRequest)?.schedulingPriority;
      const isStatOrUrgent = isStatOrUrgentPriority(priority);

      return {
        ...base,
        assignedTo: "Datavant, Staff",
        ...(isStatOrUrgent && { additionalWorkflow: [MARK_HIGH_PRIORITY_WORKFLOW] }),
      };
    }
  }

  if (classification === Classification.REFERRAL) {
    const referral = reviewedContent as Referral;

    if (referral.authorizationNumber) {
      return { ...base, assignedTo: "{authPrefix} Auth, {location}" };
    }
  }

  if (classification === Classification.MEDICAL_RECORD_REQUEST) {
    const medicalRecordRequest = reviewedContent as MedicalRecordRequest;
    const priority = medicalRecordRequest?.schedulingPriority;
    const imagingType = medicalRecordRequest?.imagingType;
    const isStatOrUrgent = isStatOrUrgentPriority(priority);
    const isAdvancedImaging = imagingType && (imagingType === ImagingType.PET || imagingType === ImagingType.CT_SCANS);

    if (!imagingType) {
      if (isStatOrUrgent) {
        return {
          ...base,
          assignedTo: providerAssignment ?? HEARTPLACE_PROVIDER_STAFF_ASSIGNMENT,
          additionalWorkflow: [MARK_HIGH_PRIORITY_WORKFLOW],
          ...(note && { note, noteLabel: "Assignment Hierarchy" }),
        };
      }

      return {
        ...base,
        assignedTo: "Datavant, Staff",
      };
    }

    if (isAdvancedImaging) {
      return {
        ...base,
        assignedTo: isStatOrUrgent ? "HP {location}, Staff" : "HP Advanced Imaging, Staff",
        additionalWorkflow: isStatOrUrgent ? [MARK_HIGH_PRIORITY_WORKFLOW] : null,
      };
    }

    return {
      ...base,
      assignedTo: "HP {location}, Staff",
      additionalWorkflow: isStatOrUrgent ? [MARK_HIGH_PRIORITY_WORKFLOW] : null,
    };
  }

  if (
    faxRecipientNumber === BEDFORD_IMAGING_FAX_NUMBER &&
    baseMapping.assignedTo === HEARTPLACE_PROVIDER_STAFF_ASSIGNMENT
  ) {
    return { ...base, assignedTo: HEARD_STAFF_ASSIGNMENT };
  }

  if (base.assignedTo === HEARTPLACE_PROVIDER_STAFF_ASSIGNMENT) {
    return {
      ...base,
      assignedTo: providerAssignment ?? HEARTPLACE_PROVIDER_STAFF_ASSIGNMENT,
      ...(note && { note, noteLabel: "Assignment Hierarchy" }),
    };
  }

  return base;
}

type HeartplaceFaxRecipientMapping = {
  referralLocation: string;
  authLocation: string;
  staffLocation: string;
  /** Cobalt / ECW facility id (`location.ehr_id` in DB). Overrides auth-location lookup when set. */
  locationEhrId?: string;
  authPrefix?: string;
  email?: string;
  providers?: string[];
};

const FAX_RECIPIENT_NUMBER_MAPPINGS: Record<string, HeartplaceFaxRecipientMapping> = {
  [PLANO_WEST_FAX_NUMBER]: {
    referralLocation: "Plano West",
    authLocation: "Plano West",
    staffLocation: "Plano West",
    locationEhrId: "182",
    email: "HP-Westfax-PlanoWest@USHeartandVascular.com",
    providers: [
      "Peterman,Mark",
      "Ramanath,Vijay",
      "Sharan,Leena",
      "Roberts,Allison",
      "Corrales,Jennifer",
      "Mathew,Preethy",
    ],
  },
  [FRISCO_FAX_NUMBER]: {
    referralLocation: "Frisco",
    authLocation: "Frisco",
    staffLocation: "Frisco",
    locationEhrId: "164",
    email: "HP-Westfax-Frisco@USHeartandVascular.com",
    providers: ["Brown,William"],
  },
  [ALLEN_FAX_NUMBER]: {
    referralLocation: "Allen",
    authLocation: "Allen",
    staffLocation: "Allen",
    locationEhrId: "156",
    email: "HP-Westfax-Allen@USHeartandVascular.com",
    providers: ["Choudhry,Amir", "Kim,Elaine"],
  },
  [ALLEN_2_FAX_NUMBER]: {
    referralLocation: "Allen",
    authLocation: "Allen",
    staffLocation: "Allen",
    locationEhrId: "156",
    email: "HP-Westfax-Allen@USHeartandVascular.com",
    providers: ["Choudhry,Amir", "Kim,Elaine"],
  },
  [HEB_FAX_NUMBER]: {
    referralLocation: "HEB",
    authLocation: "HEB",
    staffLocation: "HEB",
    locationEhrId: "169",
    email: "HP-Westfax-HEB@USHeartandVascular.com",
    providers: ["Moustapha,Ali", "Rashdan,Iyad", "Thamwiwat,Alisa"],
  },
  [PLANO_FAX_NUMBER]: {
    referralLocation: "Plano",
    authLocation: "Plano",
    staffLocation: "Plano",
    locationEhrId: "183",
    email: "HP-Westfax-Plano15@USHeartandVascular.com",
    providers: ["Dao,Timothy", "Nair,Arjun", "Duncan,John"],
  },
  [RENNER_FAX_NUMBER]: {
    referralLocation: "Renner",
    authLocation: "Renner",
    staffLocation: "Renner",
    locationEhrId: "185",
    email: "HP-Westfax-Renner@USHeartandVascular.com",
    providers: ["Ammar,Richard", "Parikh,Biren", "Serauto Canache,Astrid", "Arnautov,Simona", "Keiser,Cynthia"],
  },
  [NORTH_ARLINGTON_FAX_NUMBER]: {
    referralLocation: "Arlington",
    authLocation: "Arlington",
    staffLocation: "Arlington",
    locationEhrId: "178",
    email: "HP-Westfax-NARL@usheartandvascular.com",
    providers: ["Bheemarasetti,Manikumar", "Sharma,Shishir", "Sperry,Thomas", "Teng,Mark"],
  },
  [SOUTH_ARLINGTON_FAX_NUMBER]: {
    referralLocation: "Arlington",
    authLocation: "Arlington",
    staffLocation: "Arlington",
    locationEhrId: "682",
    email: "HP-Westfax-SARL@usheartandvascular.com",
    providers: ["Webb,Ian"],
  },
  [BAYLOR_FAX_NUMBER]: {
    referralLocation: "Baylor",
    authLocation: "Baylor",
    staffLocation: "Baylor",
    locationEhrId: "160",
    email: "HP-Westfax-Baylor@USHeartandVascular.com",
    providers: ["Feghali,Georges", "Jindal,Vikas"],
  },
  [BAYLOR_PLANO_FAX_NUMBER]: {
    referralLocation: "Baylor Plano",
    authLocation: "Baylor Plano",
    staffLocation: "Baylor Plano",
    locationEhrId: "159",
    email: "HP-Westfax-BaylorPlano@USHeartandVascular.com",
    providers: ["Woodley,Margaret", "Raza,Samreen", "Reuter,John"],
  },
  [NORTH_HILLS_FAX_NUMBER]: {
    referralLocation: "North Hills",
    authLocation: "North Hills",
    staffLocation: "North Hills",
    locationEhrId: "179",
    email: "HP-Westfax-NorthHills@USHeartandVascular.com",
    providers: ["Patel,Brijesh"],
  },
  [BEDFORD_IMAGING_FAX_NUMBER]: {
    referralLocation: "!Authorization, CBO",
    authLocation: "!Authorization, CBO",
    staffLocation: "Bedford Imaging",
    locationEhrId: "161",
    email: "HP-Westfax-BIC@usheartandvascular.com",
    providers: ["Eifling,Kelly", "Heard,Brittney"],
  },
  [GARLAND_FAX_NUMBER]: {
    referralLocation: "Garland",
    authLocation: "Garland",
    staffLocation: "Garland",
    locationEhrId: "165",
    email: "HP-Westfax-Garland@USHeartandVascular.com",
    providers: ["Patterson,Brent", "Routh,L Keith"],
  },
  [CORSICANA_FAX_NUMBER]: {
    referralLocation: "Corsicana",
    authLocation: "Corsicana",
    staffLocation: "Corsicana",
    locationEhrId: "163",
    email: "HP-Westfax-Corsicana@USHeartandVascular.com",
    providers: ["Bellomo,Joseph", "Feghali,Georges", "Jindal,Vikas"],
  },
  [MESQUITE_FAX_NUMBER]: {
    referralLocation: "Mesquite",
    authLocation: "Mesquite",
    staffLocation: "Mesquite",
    locationEhrId: "176",
    email: "HP-Westfax-Mesquite@USHeartandVascular.com",
    providers: ["Singh,Sandeep"],
  },
  [RED_OAK_FAX_NUMBER]: {
    referralLocation: "Red Oak",
    authLocation: "Red Oak",
    staffLocation: "Red Oak",
    locationEhrId: "184",
    email: "HP-Westfax-RedOak@USHeartandVascular.com",
    providers: ["Bellomo,Joseph", "Ficula,Robert"],
  },
  [PLANO_IMAGING_FAX_NUMBER]: {
    referralLocation: "!Authorization, CBO",
    authLocation: "!Authorization, CBO",
    staffLocation: "Plano Imaging",
    // TODO(locationEhrId): authLocation is "!Authorization, CBO"; the Plano Imaging Clinic-OF encounter is secondaryEhrId 171 — confirm value.
    email: "HP-Westfax-Print@usheartandvascular.com",
    providers: ["Devers,Susan"],
  },
  [MANSFIELD_FAX_NUMBER]: {
    referralLocation: "Mansfield",
    authLocation: "Mansfield",
    staffLocation: "Mansfield",
    locationEhrId: "173",
    email: "HP-Westfax-Mansfield@USHeartandVascular.com",
    providers: ["Chokshi,Binna", "Patel,Sneha", "Joco,Tiffany"],
  },
  [MEDICAL_CITY_FAX_NUMBER]: {
    referralLocation: "Medical City",
    authLocation: "Medical City",
    staffLocation: "Medical City",
    locationEhrId: "175",
    email: "HP-Westfax-MedicalCity@USHeartandVascular.com",
    providers: ["Raza,Syed", "Snyder,Richard"],
  },
  [GREENVILLE_FAX_NUMBER]: {
    referralLocation: "Mesquite",
    authLocation: "Greenville",
    staffLocation: "Greenville",
    locationEhrId: "168",
    email: "HP-Westfax-Greenville@USHeartandVascular.com",
    providers: ["Singh,Sandeep"],
  },
  [MCKINNEY_FAX_NUMBER]: {
    referralLocation: "McKinney",
    authLocation: "McKinney",
    staffLocation: "McKinney",
    locationEhrId: "174",
    email: "HP-Westfax-McKinney@USHeartandVascular.com",
    providers: ["Eades,Brian", "Vo,Trang"],
  },
  [SOUTHLAKE_FAX_NUMBER]: {
    referralLocation: "Southlake",
    authLocation: "Southlake",
    staffLocation: "Southlake",
    locationEhrId: "187",
    email: "HP-Westfax-Southlake@USHeartandVascular.com",
    providers: ["Moustapha,Ali", "Rashdan,Iyad", "Thamwiwat,Alisa"],
  },
  [ICC_FAX_NUMBER]: {
    referralLocation: "Willis, Jessica",
    authLocation: "Willis, Jessica",
    staffLocation: "Willis, Jessica",
    // TODO(locationEhrId): "Willis, Jessica" (ICC) has no secondaryEhrId in ENCOUNTER_LOCATION_ASSIGNMENT_MAPPING — confirm value.
    email: "HP-Westfax-ICC@USHeartandVascular.com",
    providers: ["Willis,Jessica"],
  },
  [CAMPBELL_FAX_NUMBER]: {
    referralLocation: "Campbell",
    authLocation: "Campbell",
    staffLocation: "Campbell",
    locationEhrId: "162",
    email: "HP-Westfax-Richardson@USHeartandVascular.com",
    providers: ["Rahimizadeh,Hootan"],
  },
  [MID_CITIES_FAX_NUMBER]: {
    referralLocation: "Plano West",
    authLocation: "Plano West",
    staffLocation: "Plano West",
    locationEhrId: "182",
    email: "HP-Westfax-MidCities@USHeartandVascular.com",
    providers: [
      "Peterman,Mark",
      "Ramanath,Vijay",
      "Sharan,Leena",
      "Roberts,Allison",
      "Corrales,Jennifer",
      "Mathew,Preethy",
    ],
  },
  [HUGULEY_FAX_NUMBER]: {
    referralLocation: "Huguley",
    authLocation: "Huguley",
    staffLocation: "Huguley",
    email: "HP-Westfax-Hugley@USHeartandVascular.com",
    providers: ["Thamwiwat,Alisa"],
  },
  [GLEN_ROSE_FAX_NUMBER]: {
    referralLocation: "Glen Rose",
    authLocation: "Glen Rose",
    staffLocation: "Glen Rose",
    locationEhrId: "838",
    email: "HP-Westfax-GlenRose@USHeartandVascular.com",
    providers: ["Patel,Deepak", "Selim,Ahmed"],
  },
  [MIDLOTHIAN_FAX_NUMBER]: {
    referralLocation: "Midlothian",
    authLocation: "Midlothian",
    staffLocation: "Midlothian",
    // TODO(locationEhrId): "HP Auth, Midlothian" has no secondaryEhrId in ENCOUNTER_LOCATION_ASSIGNMENT_MAPPING — confirm value.
    email: "HP-Westfax-Midlothian@USHeartandVascular.com",
    providers: ["Bheemarasetti,Manikumar"],
  },
  [LAS_COLINAS_FAX_NUMBER]: {
    referralLocation: "Las Colinas",
    authLocation: "Las Colinas",
    staffLocation: "Las Colinas",
    locationEhrId: "172",
    email: "HP-Westfax-LasColinas@USHeartandVascular.com",
    providers: ["Overbeck,John"],
  },
  [MISTLETOE_FAX_NUMBER]: {
    referralLocation: "Mistletoe",
    authLocation: "Mistletoe",
    staffLocation: "Mistletoe",
    locationEhrId: "743",
    authPrefix: "FWH",
    email: "fw-westfax-mistletoe@usheartandvascular.com",
    providers: [
      "Asawaeer,Majid",
      "Bhargava,Sanjay",
      "D'Souza,Denzil",
      "Kalaria,Vijay",
      "Khan,Madiha",
      "Lincoln,Chloe Myers",
      "Mushtaq,Muzammil",
      "Selim,Ahmed",
    ],
  },
  [GRANBURY_FAX_NUMBER]: {
    referralLocation: "Granbury",
    authLocation: "Granbury",
    staffLocation: "Granbury",
    locationEhrId: "744",
    authPrefix: "FWH",
    email: "fw-westfax-mistletoe@usheartandvascular.com",
    providers: ["Bhargava,Sanjay", "D'Souza,Denzil", "Kalaria,Vijay"],
  },
  [MIRA_VISTA_FAX_NUMBER]: {
    referralLocation: "South",
    authLocation: "South",
    staffLocation: "South",
    locationEhrId: "746",
    authPrefix: "FWH",
    email: "fw-westfax-mistletoe@usheartandvascular.com",
    providers: ["Liao,Lianxi", "Patel,Deepak", "Patel,Falguni"],
  },
  [FORT_WORTH_NORTH_ARLINGTON_FAX_NUMBER]: {
    referralLocation: "Mistletoe",
    authLocation: "Mistletoe",
    staffLocation: "Mistletoe",
    locationEhrId: "743",
    authPrefix: "FWH",
    email: "fw-westfax-mistletoe@usheartandvascular.com",
    providers: ["Asawaeer,Majid", "Bhargava,Sanjay", "D'Souza,Denzil", "Kalaria,Vijay", "Khan,Madiha", "Selim,Ahmed"],
  },
};

const NPI_LOCATION_MAPPING: Record<string, { referralLocation: string[]; authLocation: string[] }> = {
  "1295921989": { referralLocation: ["HP Eifling, Referrals"], authLocation: [] },
  "1164530739": { referralLocation: ["HP Abrol, Referrals"], authLocation: [] },
  "1962611830": { referralLocation: ["HP Arlington, Referrals"], authLocation: [] },
  "1548479827": { referralLocation: ["HP Chhabra, Referrals"], authLocation: [] },
  "1033300074": { referralLocation: ["HP Oyenuga, Referrals"], authLocation: [] },
  "1316332638": { referralLocation: ["HP Drutel, Referrals"], authLocation: [] },
  "1386664852": { referralLocation: ["HP Sen, Referrals"], authLocation: [] },
  "1831344001": { referralLocation: ["HP Frisco, Referrals"], authLocation: ["HP Auth, Frisco"] },
  "1295793677": { referralLocation: ["HP Frisco, Referrals"], authLocation: ["HP Auth, Frisco"] },
  "1972096147": { referralLocation: ["HP Frisco, Referrals"], authLocation: ["HP Auth, Frisco"] },
  "1538878475": { referralLocation: ["HP Frisco, Referrals"], authLocation: ["HP Auth, Frisco"] },
  "1457388258": { referralLocation: ["HP Allen, Referrals"], authLocation: ["HP Auth, Allen"] },
  "1386752939": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1003841297": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1326006685": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1508276551": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1881417897": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1144536400": { referralLocation: ["HP Allen, Referrals"], authLocation: ["HP Auth, Allen"] },
  "1780109553": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1831672088": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1801100979": { referralLocation: ["HP Frisco, Referrals"], authLocation: ["HP Auth, Frisco"] },
  "1093030934": { referralLocation: ["HP Allen, Referrals"], authLocation: ["HP Auth, Allen"] },
  "1245210327": { referralLocation: ["HP Renner, Referrals"], authLocation: ["HP Auth, Renner"] },
  "1265501985": { referralLocation: ["HP Renner, Referrals"], authLocation: ["HP Auth, Renner"] },
  "1619351996": { referralLocation: ["HP Renner, Referrals"], authLocation: ["HP Auth, Renner"] },
  "1003559428": { referralLocation: ["HP Renner, Referrals"], authLocation: ["HP Auth, Renner"] },
  "1407254501": { referralLocation: ["HP Renner, Referrals"], authLocation: ["HP Auth, Renner"] },
  "1124473160": { referralLocation: ["HP Plano, Referrals"], authLocation: ["HP Auth, Plano"] },
  "1205135985": { referralLocation: ["HP Baylor Plano, Referrals"], authLocation: ["HP Auth, Baylor Plano"] },
  "1861518177": { referralLocation: ["HP Baylor Plano, Referrals"], authLocation: ["HP Auth, Baylor Plano"] },
  "1730656737": { referralLocation: ["HP Baylor Plano, Referrals"], authLocation: ["HP Auth, Baylor Plano"] },
  "1740244730": {
    referralLocation: ["HP HEB, Referrals", "HP Southlake, Referrals"],
    authLocation: ["HP Auth, HEB", "HP Auth, Southlake"],
  },
  "1811955123": { referralLocation: ["HP HEB, Referrals"], authLocation: ["HP Auth, HEB"] },
  "1649565953": {
    referralLocation: ["HP HEB, Referrals", "HP Southlake, Referrals"],
    authLocation: ["HP Auth, HEB", "HP Auth, Southlake"],
  },
  "1497986533": { referralLocation: ["HP Arlington, Referrals"], authLocation: ["HP Auth, Arlington"] },
  "1669735734": { referralLocation: ["HP Arlington, Referrals"], authLocation: ["HP Auth, Arlington"] },
  "1578977856": { referralLocation: ["HP Arlington, Referrals"], authLocation: ["HP Auth, Arlington"] },
  "1932167210": { referralLocation: ["HP Arlington, Referrals"], authLocation: ["HP Auth, Arlington"] },
  "1558771667": { referralLocation: ["HP Mansfield, Referrals"], authLocation: ["HP Auth, Mansfield"] },
  "1508865536": { referralLocation: ["HP Las Colinas, Referrals"], authLocation: ["HP Auth, Las Colinas"] },
  "1215344924": { referralLocation: ["HP Arlington, Referrals"], authLocation: ["HP Auth, Arlington"] },
  "1538151170": { referralLocation: ["HP Mckinney, Referrals"], authLocation: ["HP Auth, Mckinney"] },
  "1982866968": { referralLocation: ["HP Plano, Referrals"], authLocation: ["HP Auth, Plano"] },
  "1407029689": { referralLocation: ["HP Plano, Referrals"], authLocation: ["HP Auth, Plano"] },
  "1396844064": { referralLocation: ["HP Plano, Referrals"], authLocation: ["HP Auth, Plano"] },
  "1083898365": { referralLocation: ["HP Plano, Referrals"], authLocation: ["HP Auth, Plano"] },
  "1932306651": { referralLocation: ["HP North Hills, Referrals"], authLocation: ["HP Auth, North Hills"] },
  "1083371728": { referralLocation: ["!Authorization,CBO"], authLocation: ["!Authorization,CBO"] },
  "1043976350": { referralLocation: ["!Authorization,CBO"], authLocation: ["!Authorization,CBO"] },
  "1740398304": {
    referralLocation: ["HP White Rock, Referrals", "HP Garland, Referrals"],
    authLocation: ["HP Auth, White Rock", "HP Auth, Garland"],
  },
  "1487615274": {
    referralLocation: ["HP White Rock, Referrals", "HP Garland, Referrals"],
    authLocation: ["HP Auth, White Rock", "HP Auth, Garland"],
  },
  "1053375550": {
    referralLocation: ["HP White Rock, Referrals", "HP Garland, Referrals"],
    authLocation: ["HP Auth, White Rock", "HP Auth, Garland"],
  },
  "1295702090": {
    referralLocation: ["HP Red Oak, Referrals", "HP White Rock"],
    authLocation: ["HP Auth, White Rock", "HP Auth, Garland"],
  },
  "1194744011": { referralLocation: ["HP Baylor, Referrals"], authLocation: ["HP Auth, Baylor"] },
  "1033126321": { referralLocation: ["HP Baylor, Referrals"], authLocation: ["HP Auth, Baylor"] },
  "1821260670": { referralLocation: ["HP Mesquite, Referrals"], authLocation: ["HP Auth, Mesquite"] },
  "1811265085": { referralLocation: ["HP Red Oak, Referrals"], authLocation: ["HP Auth, Red Oak"] },
  "1649598210": { referralLocation: ["!Authorization,CBO"], authLocation: ["!Authorization,CBO"] },
  "1669676136": { referralLocation: ["HP Mansfield, Referrals"], authLocation: ["HP Auth, Mansfield"] },
  "1780153775": { referralLocation: ["HP Mansfield, Referrals"], authLocation: ["HP Auth, Mansfield"] },
  "1144417569": { referralLocation: ["HP Medical City, Referrals"], authLocation: ["HP Auth, Medical City"] },
  "1821046582": { referralLocation: ["HP Medical City, Referrals"], authLocation: ["HP Auth, Medical City"] },
  "1619300027": { referralLocation: ["HP Mckinney, Referrals"], authLocation: ["HP Auth, Mckinney"] },
  "1942264932": { referralLocation: ["HP Southlake, Referrals"], authLocation: [] },
  "1760153498": { referralLocation: ["HP Plano West, Referrals"], authLocation: ["HP Auth, Plano West"] },
  "1336107507": { referralLocation: ["HP Campbell, Referrals"], authLocation: ["HP Auth, Campbell"] },
  "1760641377": { referralLocation: ["HP Plano, Referrals"], authLocation: ["HP Auth, Plano"] },
  "1922426204": { referralLocation: ["HP Heard, Referrals"], authLocation: [] },
};

interface EncounterLocationAssignmentMapping {
  EHRID?: string;
  LocationName: string;
  /** Cobalt / ECW facility id used for document assignment via webecw (`location.ehr_id` in DB). */
  secondaryEhrId?: string;
  /** When true, this location is excluded from scheduling region location allowlists. */
  excludeFromScheduling?: true;
  region: string;
}

export const ENCOUNTER_LOCATION_ASSIGNMENT_MAPPING: Record<string, EncounterLocationAssignmentMapping> = {
  // Heartplace Allen-OF
  "ZZ4axUtUKSnBidVZ2-oNd-P-bTgY3m1B4osWzUAypiQ": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd6plFnKTKO1MYSzoLQ52.Aw",
    LocationName: "HP Auth, Allen",
    secondaryEhrId: "156",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Alliance-OF
  "ZZ4axUtUKSnBidVZ2-oNd2KYqHq313TyuVXrzde1egg": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdzEi2vM8DWyCoo1fzcimnYw",
    LocationName: "HP Auth, North Hills",
    secondaryEhrId: "157",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Baylor Plano-OF
  "ZZ4axUtUKSnBidVZ2-oNd9E..OXHKNVruV87dtTeQmI": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd-nCJNuAGL3hcGGB0.2mVRU",
    LocationName: "HP Auth, Baylor Plano",
    secondaryEhrId: "159",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Baylor-OF
  "ZZ4axUtUKSnBidVZ2-oNd1cYaAdHSne8cVjInZ2Jf24": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd0u8lugDo0bXOGyahvR9KWg",
    LocationName: "HP Auth, Baylor",
    secondaryEhrId: "160",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Bedford Imaging Clinic-OF
  "ZZ4axUtUKSnBidVZ2-oNd4b0-W.7CPRUjfgtYK.sMto": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdytNkdQINE2hDo28bss9tRs",
    LocationName: "!Authorization, CBO",
    secondaryEhrId: "161",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Campbell-OF
  "ZZ4axUtUKSnBidVZ2-oNd1jSPEdikfPlzosgL3xu6nY": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd7mXM0idqUFkdrR0Vj2vPGY",
    LocationName: "HP Auth, Campbell",
    secondaryEhrId: "162",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Corsicana-OF
  "ZZ4axUtUKSnBidVZ2-oNd0fh57KSivltp..NZntRLoo": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd7duB1vbk6mRznLz1.zu-7s",
    LocationName: "HP Auth, Corsicana",
    region: HEARTPLACE_REGION_HEARTPLACE,
    secondaryEhrId: "163",
  },
  // Heartplace Frisco-OF
  "ZZ4axUtUKSnBidVZ2-oNd5v9rs7--eHT1dRp212u8So": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd09jRAobLszxys-cprbwKCg",
    LocationName: "HP Auth, Frisco",
    region: HEARTPLACE_REGION_HEARTPLACE,
    secondaryEhrId: "164",
  },
  // Heartplace Garland-OF
  "ZZ4axUtUKSnBidVZ2-oNd2sCLmTqhZy1Fc.dhnabdn8": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdx6xxkkjwCh-bSKtfyj7Fa4",
    LocationName: "HP Auth, Garland",
    secondaryEhrId: "165",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Glen Rose-OF
  "ZZ4axUtUKSnBidVZ2-oNd4DzSlsK2Vev7el4XsNc7qQ": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd2qD6gIvQmXOpcr6NnLRZMw",
    LocationName: "HP Auth, Glen Rose",
    secondaryEhrId: "838",
    region: HEARTPLACE_REGION_FORT_WORTH,
  },
  // Heartplace Granbury-OF
  "ZZ4axUtUKSnBidVZ2-oNd3-WpCJVgwEHJpqfG5wSchw": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdx1hEnO43NupF4.BFlBbUQg",
    LocationName: "HP Auth, Granbury",
    secondaryEhrId: "167",
    excludeFromScheduling: true,
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Greenville-OF
  "ZZ4axUtUKSnBidVZ2-oNdwqLLOUxMbJKdn-XRta1Suw": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdxHFUCiPwoH1b-zHIsTARvI",
    LocationName: "HP Auth, Greenville",
    secondaryEhrId: "168",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace HEB-OF
  "ZZ4axUtUKSnBidVZ2-oNd7tqJOiExY0R44Y1ZkNrF2U": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdz8F3gjlRTdVM0GwicNhglE",
    LocationName: "HP Auth, HEB",
    secondaryEhrId: "169",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Huguley-OF
  "ZZ4axUtUKSnBidVZ2-oNdzV3JP37pHdj72E.jcw8Lf0": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd0V3jeK.Xeuzz6t161IaZLI",
    LocationName: "HP Auth, Huguley",
    secondaryEhrId: "170",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Integrated Care Clinic Plano-OF
  "ZZ4axUtUKSnBidVZ2-oNd4k-y-fB6DT6g04seo-joTk": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd.uL23YSeEBPjL3BJypxPaY",
    LocationName: "Willis, Jessica",
    secondaryEhrId: "171",
    excludeFromScheduling: true,
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Las Colinas-OF
  "ZZ4axUtUKSnBidVZ2-oNd2V6nWyd-JgQ7RzD9jtv6DE": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd.By7ohDaOlx.Jcul.uB4YM",
    LocationName: "HP Auth, Las Colinas",
    secondaryEhrId: "172",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Mansfield-OF
  "ZZ4axUtUKSnBidVZ2-oNd63XbD4iBzzGHjVpmnqurGE": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd1FoIYYsfz-gWja3Nn5pHnM",
    LocationName: "HP Auth, Mansfield",
    secondaryEhrId: "173",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Mckinney-OF
  "ZZ4axUtUKSnBidVZ2-oNd6nFi3zMWPtypCu47feVosA": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd7ZGai9ewtkmanZbKCQAKYw",
    LocationName: "HP Auth, Mckinney",
    secondaryEhrId: "174",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Medical City-OF
  "ZZ4axUtUKSnBidVZ2-oNd0I9uC29uVFLEdoMiEfIq.k": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd986t5wnl-ObYYTEao.4Q3U",
    LocationName: "HP Auth, Medical City",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Mesquite-OF
  "ZZ4axUtUKSnBidVZ2-oNd2dXBvjCPPOPVzs9UHfU2Rs": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd.7.NR6L7hf12MamBJCc7Os",
    LocationName: "HP Auth,Mesquite",
    secondaryEhrId: "176",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Midlothian-OF
  "ZZ4axUtUKSnBidVZ2-oNd7cvEhWXYIJeh07nQHYjpmg": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd.bU20VhXB.tn2gSy2wGMFQ",
    LocationName: "HP Auth, Midlothian",
    secondaryEhrId: "177",
    excludeFromScheduling: true,
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Arlington-OF
  "ZZ4axUtUKSnBidVZ2-oNd75.HOfPqKbj7s.0EDy15GU": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd65rVqHSENOGKsGA9M-V5D4",
    LocationName: "HP Auth, Arlington",
    secondaryEhrId: "178",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace North Hills-OF
  "ZZ4axUtUKSnBidVZ2-oNd76qUpnhHnEePDTyWZm9GO8": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdzEi2vM8DWyCoo1fzcimnYw",
    LocationName: "HP Auth, North Hills",
    secondaryEhrId: "179",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Pecan Plantation-OF
  "ZZ4axUtUKSnBidVZ2-oNdwb-53wifMape8jXeSW2oQg": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd688nX4Sjv0yquEY11jxF40",
    LocationName: "HP Auth, Pecan Plantation",
    secondaryEhrId: "180",
    excludeFromScheduling: true,
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Plano Imaging Clinic-OF
  "ZZ4axUtUKSnBidVZ2-oNdxJxqihDQIM8XPDem7O-3VI": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdytNkdQINE2hDo28bss9tRs",
    LocationName: "!Authorization, CBO",
    secondaryEhrId: "181",
    excludeFromScheduling: true,
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Plano West-OF
  "ZZ4axUtUKSnBidVZ2-oNd6a1J6708BAvU3ec7yJXvW0": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdwfIaJQRHVXoDE-rOGVuoUU",
    LocationName: "HP Auth, Plano West",
    secondaryEhrId: "182",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Plano-OF
  "ZZ4axUtUKSnBidVZ2-oNd5UXD7LUX42vP.bTLgnXhDE": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdwVBFGjDmZ3FeUZofmHxW9o",
    LocationName: "HP Auth, Plano",
    secondaryEhrId: "183",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Red Oak-OF
  "ZZ4axUtUKSnBidVZ2-oNd6I0LN4WlbCy7wxds74gLdU": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd5ktP6CZ6D781JBrldWwK3w",
    LocationName: "HP Auth, Red Oak",
    region: HEARTPLACE_REGION_HEARTPLACE,
    secondaryEhrId: "184",
  },
  // Heartplace Renner-OF
  "ZZ4axUtUKSnBidVZ2-oNd9oNtoQuGSxPuLFjgnO6Hf0": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd1fTwrhCWsoCmFXbEba64kU",
    LocationName: "HP Auth, Renner",
    secondaryEhrId: "185",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Arlington-OF
  "ZZ4axUtUKSnBidVZ2-oNdze7LOCrUdpoYvXWCBCOonk": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd4Rw8pz3BKJt0tXItcyUKHg",
    LocationName: "HP Auth, Arlington",
    secondaryEhrId: "682",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Southlake-OF
  "ZZ4axUtUKSnBidVZ2-oNd2pvNUFNPYwKgLTpDDpM0Bk": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd02NIQeEoXusZ9URJJD5CH4",
    LocationName: "HP Auth, Southlake",
    secondaryEhrId: "187",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace Stephenville-OF
  "ZZ4axUtUKSnBidVZ2-oNd5OLd0MEJlyxMyt8l24nOMg": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd.By7ohDaOlx.Jcul.uB4YM",
    LocationName: "HP Auth, Las Colinas",
    secondaryEhrId: "188",
    excludeFromScheduling: true,
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
  // Heartplace White Rock-OF
  "ZZ4axUtUKSnBidVZ2-oNd1cqHP3qQvoeRouv9NuOM.g": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd66Or4qqyF3XK6L6.f6EuFE",
    LocationName: "HP Auth, White Rock",
    region: HEARTPLACE_REGION_HEARTPLACE,
    secondaryEhrId: "190",
  },
  // Heartplace North Arlington-OF (FWH referrals)
  "ZZ4axUtUKSnBidVZ2-oNd6Cc7lgLHQ3kI9-13oW0nuI": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdx97Qm.xbIcwq3P2c3qsDWU",
    LocationName: "HP Arlington, Referrals",
    secondaryEhrId: "821",
    region: HEARTPLACE_REGION_FORT_WORTH,
  },
  // Fort Worth Heart Mistletoe-OF
  "ZZ4axUtUKSnBidVZ2-oNd22b.xUJ6CAA.36SHat4Sx4": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdxCfXIkN57zmzkBF9cfwuso",
    LocationName: "FWH Auth, Mistletoe",
    secondaryEhrId: "743",
    region: HEARTPLACE_REGION_FORT_WORTH,
  },
  // Fort Worth Granbury
  "ZZ4axUtUKSnBidVZ2-oNd8X5.lVX8cmkPuH1c-GzIsM": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd4DEK05PR5dd9qtTHHCck2I",
    LocationName: "FWH Auth, Granbury",
    secondaryEhrId: "744",
    region: HEARTPLACE_REGION_FORT_WORTH,
  },
  // Fort Worth Heart Mira Vista-OF
  "ZZ4axUtUKSnBidVZ2-oNd.oaNwJeuTd1bmUmeMXQS14": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNd.T8Y3yjK.sslwBV3jIkvqk",
    LocationName: "FWH Auth, Mira Vista",
    secondaryEhrId: "745",
    region: HEARTPLACE_REGION_FORT_WORTH,
  },
  // Fort Worth South
  "ZZ4axUtUKSnBidVZ2-oNd.F.lFE5Jz0zQq6Tt8g-fzc": {
    EHRID: "ZZ4axUtUKSnBidVZ2-oNdzsWwLqt-62LjD9IDdWj0dQ",
    LocationName: "FWH Auth, South",
    secondaryEhrId: "746",
    region: HEARTPLACE_REGION_FORT_WORTH,
  },
  // Dallas North (scheduling-only; no CloudCruise encounter assignment key). Medical City was renamed to Dallas North.
  "HEARTPLACE_DALLAS_NORTH_OF": {
    LocationName: "Heartplace Medical City-OF",
    secondaryEhrId: "175",
    region: HEARTPLACE_REGION_HEARTPLACE,
  },
};

export const HEARTPLACE_WEBECW_LOCATION_ASSIGNMENT_MAPPING: Record<string, EncounterLocationAssignmentMapping> =
  Object.fromEntries(
    Object.values(ENCOUNTER_LOCATION_ASSIGNMENT_MAPPING)
      .filter((mapping): mapping is EncounterLocationAssignmentMapping & { secondaryEhrId: string } =>
        Boolean(mapping.secondaryEhrId)
      )
      .map((mapping) => [mapping.secondaryEhrId, mapping])
  );

export const HEARTPLACE_WEBECW_ENCOUNTER_EXCLUSIONS: EcwEncounterExclusions = {
  statuses: [EncounterStatus.CANCELLED, EncounterStatus.PLANNED],
  visitTypes: [EncounterType.MIGRATED, EncounterType.WEB],
};

export async function getHeartplaceNpiLocationMappingForNpi(
  task: Task
): Promise<{ referralLocation: string; authLocation: string | null } | null> {
  const referral = task.referral;

  if (!referral) {
    logAndThrow("Task does not have a referral when trying to get NPI location mapping", {
      task: task.id,
    });
  }

  const receivingProvider = referral.receivingProvider;

  if (!receivingProvider) {
    return null;
  }

  const npi = receivingProvider.npiNumber;

  if (!npi) {
    return null;
  }

  const mapping = NPI_LOCATION_MAPPING[npi];

  if (!mapping || mapping.referralLocation.length === 0) {
    return null;
  }

  // Use deterministic selection based on task ID to ensure consistency
  const randomValue = await uuidToRandom(task.id);
  const referralIndex = Math.floor(randomValue * mapping.referralLocation.length);
  const authIndex = mapping.authLocation.length > 0 ? Math.floor(randomValue * mapping.authLocation.length) : -1;

  return {
    referralLocation: mapping.referralLocation[referralIndex],
    authLocation: authIndex >= 0 ? mapping.authLocation[authIndex] : null,
  };
}

export function getHeartplaceFaxLocationMappingForFaxRecipientNumber(faxRecipientNumber: string): {
  referralLocation: string;
  authLocation: string;
  staffLocation: string;
  faxRecipientNumber: string;
  authPrefix?: string;
} | null {
  const mapping = FAX_RECIPIENT_NUMBER_MAPPINGS[faxRecipientNumber];

  if (!mapping) return null;

  return {
    referralLocation: mapping.referralLocation,
    authLocation: mapping.authLocation,
    staffLocation: mapping.staffLocation,
    faxRecipientNumber: faxRecipientNumber,
    ...(mapping.authPrefix && { authPrefix: mapping.authPrefix }),
  };
}

export function normalizeToFwhPrefix(str: string): string {
  return str.replace(/^(?:HP|FW) /, "FWH ");
}

export function extractLocationFromEncounterLocationName(locationName: string): string | null {
  const authMatch = locationName.match(/^(?:HP|FWH) Auth,\s*(.+)$/);

  if (authMatch) return authMatch[1];

  const referralMatch = locationName.match(/^(?:HP|FWH) (.+), Referrals$/);

  if (referralMatch) return referralMatch[1];

  return null;
}

export function normalizeEncounterLocationName(locationName: string): string {
  return extractLocationFromEncounterLocationName(locationName) ?? locationName;
}

const ICC_PLANO_TEAM = "ICC Plano, Team";
const ICC_HOUSTON_TEAM = "ICC Houston, Team";
const ICC_TUCSON_TEAM = "ICC Tucson, Team";

const PLANO_PREFIXES = ["HP ", "FWH "];
const HOUSTON_PREFIXES = ["MKC "];
const HOUSTON_KEYWORDS = ["Orion", "HCA "];
const TUCSON_KEYWORDS = ["PIMA"];

// "Willis, Jessica" is the ICC practitioner name used as a location identifier in EHR
const ICC_PRACTITIONER_LOCATION = "Willis, Jessica";

export function resolveIccTeamFromEncounterLocationName(locationName: string): string | null {
  const name = locationName.toLowerCase();

  if (
    PLANO_PREFIXES.some((p) => name.startsWith(p.toLowerCase())) ||
    name === ICC_PRACTITIONER_LOCATION.toLowerCase()
  ) {
    return ICC_PLANO_TEAM;
  }

  if (
    HOUSTON_PREFIXES.some((p) => name.startsWith(p.toLowerCase())) ||
    HOUSTON_KEYWORDS.some((k) => name.includes(k.toLowerCase()))
  ) {
    return ICC_HOUSTON_TEAM;
  }

  if (TUCSON_KEYWORDS.some((k) => name.includes(k.toLowerCase()))) {
    return ICC_TUCSON_TEAM;
  }

  return null;
}

const ICC_NPI_TO_TEAM: Record<string, string> = {
  // HeartPlace → ICC Plano
  "1831344001": ICC_PLANO_TEAM,
  "1457388258": ICC_PLANO_TEAM,
  "1386752939": ICC_PLANO_TEAM,
  "1003841297": ICC_PLANO_TEAM,
  "1326006685": ICC_PLANO_TEAM,
  "1508276551": ICC_PLANO_TEAM,
  "1144536400": ICC_PLANO_TEAM,
  "1780109553": ICC_PLANO_TEAM,
  "1831672088": ICC_PLANO_TEAM,
  "1801100979": ICC_PLANO_TEAM,
  "1093030934": ICC_PLANO_TEAM,
  "1245210327": ICC_PLANO_TEAM,
  "1265501985": ICC_PLANO_TEAM,
  "1619351996": ICC_PLANO_TEAM,
  "1003559428": ICC_PLANO_TEAM,
  "1407254501": ICC_PLANO_TEAM,
  "1124473160": ICC_PLANO_TEAM,
  "1205135985": ICC_PLANO_TEAM,
  "1861518177": ICC_PLANO_TEAM,
  "1730656737": ICC_PLANO_TEAM,
  "1740244730": ICC_PLANO_TEAM,
  "1811955123": ICC_PLANO_TEAM,
  "1649565953": ICC_PLANO_TEAM,
  "1497986533": ICC_PLANO_TEAM,
  "1295921989": ICC_PLANO_TEAM,
  "1669735734": ICC_PLANO_TEAM,
  "1578977856": ICC_PLANO_TEAM,
  "1932167210": ICC_PLANO_TEAM,
  "1962611830": ICC_PLANO_TEAM,
  "1558771667": ICC_PLANO_TEAM,
  "1508865536": ICC_PLANO_TEAM,
  "1215344924": ICC_PLANO_TEAM,
  "1548479827": ICC_PLANO_TEAM,
  "1538151170": ICC_PLANO_TEAM,
  "1982866968": ICC_PLANO_TEAM,
  "1932306651": ICC_PLANO_TEAM,
  "1386664852": ICC_PLANO_TEAM,
  "1083371728": ICC_PLANO_TEAM,
  "1043976350": ICC_PLANO_TEAM,
  "1740398304": ICC_PLANO_TEAM,
  "1487615274": ICC_PLANO_TEAM,
  "1053375550": ICC_PLANO_TEAM,
  "1295702090": ICC_PLANO_TEAM,
  "1194744011": ICC_PLANO_TEAM,
  "1033126321": ICC_PLANO_TEAM,
  "1821260670": ICC_PLANO_TEAM,
  "1811265085": ICC_PLANO_TEAM,
  "1649598210": ICC_PLANO_TEAM,
  "1669676136": ICC_PLANO_TEAM,
  "1780153775": ICC_PLANO_TEAM,
  "1033300074": ICC_PLANO_TEAM,
  "1144417569": ICC_PLANO_TEAM,
  "1821046582": ICC_PLANO_TEAM,
  "1619300027": ICC_PLANO_TEAM,
  "1164530739": ICC_PLANO_TEAM,
  "1942264932": ICC_PLANO_TEAM,
  "1316332638": ICC_PLANO_TEAM,
  "1760153498": ICC_PLANO_TEAM,
  "1336107507": ICC_PLANO_TEAM,
  "1083898365": ICC_PLANO_TEAM,
  "1760641377": ICC_PLANO_TEAM,
  "1215991906": ICC_PLANO_TEAM,
  "1922426204": ICC_PLANO_TEAM,
  "1881417897": ICC_PLANO_TEAM,
  "1295793677": ICC_PLANO_TEAM,
  "1972096147": ICC_PLANO_TEAM,
  "1538878475": ICC_PLANO_TEAM,
  "1396844064": ICC_PLANO_TEAM,
  "1407029689": ICC_PLANO_TEAM,
  // Fort Worth → ICC Plano (1457337057 also appears as a HeartPlace provider; same team)
  "1730496829": ICC_PLANO_TEAM,
  "1891734083": ICC_PLANO_TEAM,
  "1457337057": ICC_PLANO_TEAM,
  "1205811031": ICC_PLANO_TEAM,
  "1043296502": ICC_PLANO_TEAM,
  "1972551984": ICC_PLANO_TEAM,
  "1386950368": ICC_PLANO_TEAM,
  "1649369885": ICC_PLANO_TEAM,
  "1780368837": ICC_PLANO_TEAM,
  "1316752991": ICC_PLANO_TEAM,
  "1093977639": ICC_PLANO_TEAM,
  "1134651169": ICC_PLANO_TEAM,
  // Memorial Katy Cardiology Associates → ICC Houston
  "1932147303": ICC_HOUSTON_TEAM,
  "1538124466": ICC_HOUSTON_TEAM,
  "1457302879": ICC_HOUSTON_TEAM,
  "1588629497": ICC_HOUSTON_TEAM,
  "1790938538": ICC_HOUSTON_TEAM,
  "1306860457": ICC_HOUSTON_TEAM,
  "1689929150": ICC_HOUSTON_TEAM,
  "1487838256": ICC_HOUSTON_TEAM,
  "1891104915": ICC_HOUSTON_TEAM,
  "1114451002": ICC_HOUSTON_TEAM,
  // Orion & Houston Cardiovascular Associates → ICC Houston
  "1407902794": ICC_HOUSTON_TEAM,
  "1538195938": ICC_HOUSTON_TEAM,
  "1114274149": ICC_HOUSTON_TEAM,
  "1669609277": ICC_HOUSTON_TEAM,
  "1245557396": ICC_HOUSTON_TEAM,
  "1609131929": ICC_HOUSTON_TEAM,
  // PIMA → ICC Tucson
  "1487632212": ICC_TUCSON_TEAM,
  "1679551485": ICC_TUCSON_TEAM,
  "1023011269": ICC_TUCSON_TEAM,
  "1679551386": ICC_TUCSON_TEAM,
  "1912962804": ICC_TUCSON_TEAM,
  "1952562647": ICC_TUCSON_TEAM,
  "1326350562": ICC_TUCSON_TEAM,
  "1699076596": ICC_TUCSON_TEAM,
  "1164425096": ICC_TUCSON_TEAM,
  "1457670440": ICC_TUCSON_TEAM,
  "1639147382": ICC_TUCSON_TEAM,
  "1043298656": ICC_TUCSON_TEAM,
  "1477531754": ICC_TUCSON_TEAM,
  "1417077603": ICC_TUCSON_TEAM,
  "1679551295": ICC_TUCSON_TEAM,
  "1548248057": ICC_TUCSON_TEAM,
  "1194727438": ICC_TUCSON_TEAM,
  "1487632659": ICC_TUCSON_TEAM,
  "1093793267": ICC_TUCSON_TEAM,
  "1891083812": ICC_TUCSON_TEAM,
  "1417165150": ICC_TUCSON_TEAM,
  "1306285325": ICC_TUCSON_TEAM,
  "1811004435": ICC_TUCSON_TEAM,
  "1609980499": ICC_TUCSON_TEAM,
  "1538426069": ICC_TUCSON_TEAM,
  "1477531713": ICC_TUCSON_TEAM,
  "1669137741": ICC_TUCSON_TEAM,
  "1982109138": ICC_TUCSON_TEAM,
  "1639651896": ICC_TUCSON_TEAM,
  "1003782509": ICC_TUCSON_TEAM,
  "1285015503": ICC_TUCSON_TEAM,
  "1326270059": ICC_TUCSON_TEAM,
  "1124625140": ICC_TUCSON_TEAM,
  "1437682051": ICC_TUCSON_TEAM,
  "1730672262": ICC_TUCSON_TEAM,
  "1649904731": ICC_TUCSON_TEAM,
  "1649611336": ICC_TUCSON_TEAM,
  "1235433426": ICC_TUCSON_TEAM,
  "1881461861": ICC_TUCSON_TEAM,
  "1043216872": ICC_TUCSON_TEAM,
};

export function resolveIccTeamFromProviderNpi(npi: string): string | null {
  return ICC_NPI_TO_TEAM[npi] ?? null;
}

export function getHeartplaceEmailMappingForFaxRecipientNumber(faxRecipientNumber: string): string | null {
  return FAX_RECIPIENT_NUMBER_MAPPINGS[faxRecipientNumber]?.email || null;
}

export function getHeartplaceProvidersForFaxRecipientNumber(faxRecipientNumber: string): string[] | null {
  return FAX_RECIPIENT_NUMBER_MAPPINGS[faxRecipientNumber]?.providers || null;
}

/**
 * Resolves the receiving location's EHR id (Cobalt/ECW `location.ehr_id`) for a fax recipient number.
 * Intended as a fallback for scheduling when a referral has no receiving location: the same value as
 * `referral.receivingLocation.ehrId` and `ENCOUNTER_LOCATION_ASSIGNMENT_MAPPING`'s `secondaryEhrId`.
 *
 * @param faxRecipientNumber - The fax recipient (WestFax) number the referral arrived on.
 * @returns The location EHR id, or null when unknown for that fax.
 */
export function getHeartplaceLocationEhrIdForFaxRecipientNumber(faxRecipientNumber: string | null): string | null {
  if (!faxRecipientNumber) return null;

  return FAX_RECIPIENT_NUMBER_MAPPINGS[faxRecipientNumber]?.locationEhrId ?? null;
}

export const ALL_HEARTPLACE_PROVIDERS: string[] = [
  ...new Set(Object.values(FAX_RECIPIENT_NUMBER_MAPPINGS).flatMap((m) => m.providers ?? [])),
];

const HEARTPLACE_FORT_WORTH_FMLA_EMAIL = "Earl.Hauss@usheartandvascular.com";

export function getHeartplaceDocRoutingStrategy(
  section: Section,
  patient: Patient,
  faxRecipientNumber: string | null = null
): DocRoutingStrategy[] {
  if (
    section.classification === Classification.ADMIN_FMLA_FORMS &&
    isHeartplaceFortWorthFaxNumber(faxRecipientNumber)
  ) {
    return [DocRoutingStrategy.EMAIL];
  }

  if (isPatientNotInEhr(patient)) {
    return [DocRoutingStrategy.MANUAL];
  }

  return [DocRoutingStrategy.CLOUD_CRUISE];
}

export function getHeartplaceDocRoutingEmailRecipients(
  faxRecipientNumber: string | null,
  classification?: Classification
): string[] {
  if (!faxRecipientNumber) return [];

  if (classification === Classification.ADMIN_FMLA_FORMS && isHeartplaceFortWorthFaxNumber(faxRecipientNumber)) {
    return [HEARTPLACE_FORT_WORTH_FMLA_EMAIL];
  }

  const email = getHeartplaceEmailMappingForFaxRecipientNumber(faxRecipientNumber);

  return email ? [email] : [];
}

export function getHeartplaceReferralRoutingStrategy(
  section: Section,
  patient: Patient,
  retryCount: number = 0
): ReferralRoutingStrategy[] {
  return [ReferralRoutingStrategy.EHR_AND_CLOUDCRUISE];
}

export function getHeartplaceAutoCloseSamplingRate(_section: Section): number {
  return 0.979;
}

export function getHeartplaceUploadClassification(classification: Classification): Classification {
  if (classification === Classification.CONSENT) {
    return Classification.MEDICAL_RECORD_REQUEST;
  }

  return classification;
}

export async function getHeartplaceAutocloseStatus(task: Task, section: Section): Promise<TaskStatus> {
  return (await isUuidSelected(task.id, getHeartplaceAutoCloseSamplingRate(section)))
    ? TaskStatus.COMPLETED
    : TaskStatus.VERIFICATION_NEEDED;
}

const HEARTPLACE_NO_AUTOCLOSE_FAX_NUMBERS = [SOUTH_ARLINGTON_FAX_NUMBER];

export function getHeartplacePostCompletionActions(
  autocloseStatus: TaskStatus,
  faxRecipientNumber: string | null,
  taskType: TaskType
): { status: TaskStatus; addTags: string[] } {
  if (taskType !== TaskType.REFERRAL) {
    return { status: autocloseStatus, addTags: [] };
  }

  if (faxRecipientNumber && HEARTPLACE_NO_AUTOCLOSE_FAX_NUMBERS.includes(faxRecipientNumber)) {
    return { status: TaskStatus.VERIFICATION_NEEDED, addTags: [] };
  }

  if (!faxRecipientNumber || !HEARTPLACE_SCHEDULING_FAX_NUMBERS.includes(faxRecipientNumber)) {
    return { status: autocloseStatus, addTags: [] };
  }

  return {
    status: autocloseStatus === TaskStatus.COMPLETED ? TaskStatus.REVIEW_NEEDED : autocloseStatus,
    addTags: [],
  };
}

export function getHeartplaceReferralTaskInitialTags(faxRecipientNumber: string | null): string[] {
  if (!faxRecipientNumber || HEARTPLACE_SCHEDULING_FAX_NUMBERS.includes(faxRecipientNumber)) {
    return [HEARTPLACE_SCHEDULING_TAG];
  }

  return [];
}

const HEARTPLACE_RECEIVING_LOCATION_TO_FAX_NUMBER: Record<string, string> = {
  "Fort Worth Heart - Glen Rose": GRANBURY_FAX_NUMBER,
  "Fort Worth Heart - Downtown": MISTLETOE_FAX_NUMBER,
  "Fort Worth Heart - Granbury": GRANBURY_FAX_NUMBER,
  "Fort Worth Heart - Mira Vista": MIRA_VISTA_FAX_NUMBER,
  "Fort Worth Heart - North Arlington": FORT_WORTH_NORTH_ARLINGTON_FAX_NUMBER,
  "Fort Worth Heart - Pecan Plantation": GRANBURY_FAX_NUMBER,
  "HeartPlace - Allen Medical Plaza": ALLEN_FAX_NUMBER,
  "HeartPlace - Alliance": NORTH_HILLS_FAX_NUMBER,
  "HeartPlace - Baylor Plano": BAYLOR_PLANO_FAX_NUMBER,
  "HeartPlace - BHVH": BAYLOR_FAX_NUMBER,
  "HeartPlace - Corsicana": CORSICANA_FAX_NUMBER,
  "HeartPlace - Dallas North": MEDICAL_CITY_FAX_NUMBER,
  "HeartPlace - Frisco": FRISCO_FAX_NUMBER,
  "HeartPlace - Garland": GARLAND_FAX_NUMBER,
  "HeartPlace - Greenville": GREENVILLE_FAX_NUMBER,
  "HeartPlace - HEB": HEB_FAX_NUMBER,
  "HeartPlace - Huguley": HUGULEY_FAX_NUMBER,
  "HeartPlace - Las Colinas": LAS_COLINAS_FAX_NUMBER,
  "HeartPlace - Mansfield": MANSFIELD_FAX_NUMBER,
  "HeartPlace - McKinney": MCKINNEY_FAX_NUMBER,
  "HeartPlace - Mesquite": MESQUITE_FAX_NUMBER,
  "HeartPlace - Methodist Richardson": CAMPBELL_FAX_NUMBER,
  "HeartPlace - North Arlington": NORTH_ARLINGTON_FAX_NUMBER,
  "HeartPlace - North Hills": NORTH_HILLS_FAX_NUMBER,
  "HeartPlace - Plano": PLANO_FAX_NUMBER,
  "HeartPlace - Plano West": PLANO_WEST_FAX_NUMBER,
  "HeartPlace - Red Oak": RED_OAK_FAX_NUMBER,
  "HeartPlace - Richardson": CAMPBELL_FAX_NUMBER,
  "HeartPlace - South Arlington": SOUTH_ARLINGTON_FAX_NUMBER,
  "HeartPlace - South Lake": SOUTHLAKE_FAX_NUMBER,
  "HeartPlace - White Rock": GARLAND_FAX_NUMBER,
};

export function getHeartplaceFaxNumberForReceivingLocationName(name: string): string | null {
  return HEARTPLACE_RECEIVING_LOCATION_TO_FAX_NUMBER[name] ?? null;
}

const HEARTPLACE_SUPPLEMENTARY_CLASSIFICATIONS = new Set<Classification>([
  Classification.ADMIN,
  Classification.BLANK,
  Classification.COVER_PAGE,
  Classification.FINANCIAL_BILLING_RECORDS,
  Classification.INTAKE,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.OTHER,
  Classification.REFERRAL_RESPONSE,
  Classification.UNKNOWN,
  Classification.ADMIN_PATIENT_QUESTIONNAIRE,
  Classification.IDENTIFICATION_CARD,
  Classification.INSURANCE_CARD,
  Classification.LEGAL,
  Classification.MEDICAL_HISTORY_MEDICATIONS,
  Classification.JUNK,
  Classification.ORDER_LAB,
]);

const HEARTPLACE_ORDER_CLASSIFICATIONS = new Set<Classification>([
  Classification.DME_ORDER,
  Classification.ORDER_CARDIAC,
  Classification.ORDER_CLARIFICATION,
  Classification.ORDER_IMAGING,
]);

const HEARTPLACE_LAB_CLASSIFICATIONS = new Set<Classification>(
  classificationToHeartplaceMap[ClassificationCategory.LAB_RESULTS]
);

const HEARTPLACE_HOSPITAL_CLASSIFICATIONS = new Set<Classification>([
  Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL,
  Classification.NOTIFICATION_HOSPITAL_DISCHARGE,
  Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
  Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
  Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY,
]);

export function getHeartplaceGroupingClassification(classifications: Classification[]): Classification | null {
  if (classifications.length <= 1) {
    return null;
  }

  const hasMedicalRecordRequest = classifications.includes(Classification.MEDICAL_RECORD_REQUEST);
  const hasReferralResponse = classifications.includes(Classification.REFERRAL_RESPONSE);
  const substantive = classifications.filter((c) => !HEARTPLACE_SUPPLEMENTARY_CLASSIFICATIONS.has(c));

  if (new Set(classifications).size !== classifications.length) {
    return null;
  }

  if (substantive.length === 0) {
    if (hasReferralResponse) {
      return Classification.REFERRAL_RESPONSE;
    }

    return hasMedicalRecordRequest ? Classification.MEDICAL_RECORD_REQUEST : null;
  }

  if (substantive.length === 1) {
    return substantive[0];
  }

  if (substantive.includes(Classification.CLEARANCE_REQUEST)) {
    return Classification.CLEARANCE_REQUEST;
  }

  if (substantive.includes(Classification.REFERRAL)) {
    return Classification.REFERRAL;
  }

  const orderClassification = substantive.find((c) => HEARTPLACE_ORDER_CLASSIFICATIONS.has(c));

  if (orderClassification) {
    return orderClassification;
  }

  if (substantive.includes(Classification.ADMIN_DISABILITY_FORMS)) {
    return Classification.ADMIN_DISABILITY_FORMS;
  }

  // RX hierarchy: Clarification > Care Consideration > Therapeutic Alert > Refill
  if (substantive.includes(Classification.PHARMACY_RELATED_CLARIFICATION)) {
    return Classification.PHARMACY_RELATED_CLARIFICATION;
  }

  if (substantive.includes(Classification.PHARMACY_RELATED_CARE_CONSIDERATION)) {
    return Classification.PHARMACY_RELATED_CARE_CONSIDERATION;
  }

  if (substantive.includes(Classification.PHARMACY_RELATED_ALERT)) {
    return Classification.PHARMACY_RELATED_ALERT;
  }

  if (substantive.includes(Classification.PHARMACY_RELATED_REFILL)) {
    return Classification.PHARMACY_RELATED_REFILL;
  }

  const isOnlyLabs = substantive.every((c) => HEARTPLACE_LAB_CLASSIFICATIONS.has(c));

  if (isOnlyLabs) {
    const uniqueLabTypes = new Set(substantive);

    if (uniqueLabTypes.size === 1) {
      return substantive[0];
    }

    return Classification.LAB_RESULTS;
  }

  const isHospital = substantive.some((c) => HEARTPLACE_HOSPITAL_CLASSIFICATIONS.has(c));

  if (isHospital) {
    if (substantive.includes(Classification.PROGRESS_NOTE_HOSPITAL_RECORDS)) {
      return Classification.PROGRESS_NOTE_HOSPITAL_RECORDS;
    }

    if (substantive.includes(Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY)) {
      return Classification.PROGRESS_NOTE_HOSPITAL_SUMMARY;
    }

    if (substantive.includes(Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY)) {
      return Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY;
    }

    return Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL;
  }

  return Classification.PROGRESS_NOTE;
}

export const HEARTPLACE_COBALT_STATUS_CODE_MAP: Record<string, AppointmentStatus> = {
  "ansph": AppointmentStatus.UNKNOWN,
  "arr": AppointmentStatus.CHECKED_IN,
  "authpend": AppointmentStatus.SCHEDULED,
  "canc": AppointmentStatus.CANCELLED,
  "cancphone": AppointmentStatus.CANCELLED,
  "cancsms": AppointmentStatus.CANCELLED,
  "cash": AppointmentStatus.UNKNOWN,
  "charged": AppointmentStatus.COMPLETED,
  "chk": AppointmentStatus.COMPLETED,
  "confphone": AppointmentStatus.SCHEDULED,
  "confsms": AppointmentStatus.SCHEDULED,
  "cvcp": AppointmentStatus.UNKNOWN,
  "dbcanc": AppointmentStatus.CANCELLED,
  "ewoc": AppointmentStatus.UNKNOWN,
  "expired": AppointmentStatus.CANCELLED,
  "failedmsg": AppointmentStatus.UNKNOWN,
  "inactive": AppointmentStatus.CANCELLED,
  "kioskarr": AppointmentStatus.CHECKED_IN,
  "lab": AppointmentStatus.UNKNOWN,
  "lwbs": AppointmentStatus.NO_SHOW,
  "metz": AppointmentStatus.UNKNOWN,
  "n/a": AppointmentStatus.UNKNOWN,
  "need atn": AppointmentStatus.SCHEDULED,
  "nuc denied": AppointmentStatus.CANCELLED,
  "n/s": AppointmentStatus.NO_SHOW,
  "nv": AppointmentStatus.UNKNOWN,
  "oon": AppointmentStatus.UNKNOWN,
  "paperwrk": AppointmentStatus.UNKNOWN,
  "pen_chkin": AppointmentStatus.SCHEDULED,
  "pen pay": AppointmentStatus.SCHEDULED,
  "pen_pay": AppointmentStatus.SCHEDULED,
  "pen": AppointmentStatus.SCHEDULED,
  "pw": AppointmentStatus.UNKNOWN,
  "qr_arr": AppointmentStatus.CHECKED_IN,
  "ref": AppointmentStatus.UNKNOWN,
  "research": AppointmentStatus.UNKNOWN,
  "rnappr": AppointmentStatus.SCHEDULED,
  "r/s": AppointmentStatus.RESCHEDULED,
  "rsphone": AppointmentStatus.RESCHEDULED,
  "schedule": AppointmentStatus.SCHEDULED,
  "sdcanc": AppointmentStatus.CANCELLED,
  "testing": AppointmentStatus.UNKNOWN,
  "verified": AppointmentStatus.SCHEDULED,
  "voicemsg": AppointmentStatus.UNKNOWN,
};

export const HEARTPLACE_COBALT_APPOINTMENT_TYPE_TO_VISIT_TYPE_MAP: Partial<Record<AppointmentType, string>> = {
  [AppointmentType.NEW_PATIENT_CHECKIN]: "New Pt",
  [AppointmentType.INITIAL_CONSULTATION]: "New Pt",
  [AppointmentType.EXISTING_PATIENT_CHECKIN]: "Est Pt",
  [AppointmentType.FOLLOW_UP]: "F/U",
};

export const HEARTPLACE_COBALT_VISIT_TYPE_MAP: Record<string, AppointmentType> = {
  "f/u 6 mths": AppointmentType.FOLLOW_UP,
  "f/u": AppointmentType.FOLLOW_UP,
  "anticoagfu": AppointmentType.FOLLOW_UP,
  "cardrehab": AppointmentType.FOLLOW_UP,
  "hosp f/u": AppointmentType.FOLLOW_UP,
  "pcm f/u": AppointmentType.FOLLOW_UP,
  "veinfu": AppointmentType.FOLLOW_UP,
  "warfarin": AppointmentType.FOLLOW_UP,
  "woundchk": AppointmentType.FOLLOW_UP,
  "postaistnt": AppointmentType.FOLLOW_UP,
  "postavfavg": AppointmentType.FOLLOW_UP,
  "postca": AppointmentType.FOLLOW_UP,
  "postevar": AppointmentType.FOLLOW_UP,
  "postilioen": AppointmentType.FOLLOW_UP,
  "postivstnt": AppointmentType.FOLLOW_UP,
  "postleasa": AppointmentType.FOLLOW_UP,
  "postlebyp": AppointmentType.FOLLOW_UP,
  "postmesint": AppointmentType.FOLLOW_UP,
  "postop": AppointmentType.FOLLOW_UP,
  "postrestnt": AppointmentType.FOLLOW_UP,
  "postz/evar": AppointmentType.FOLLOW_UP,
  "devcheckrm": AppointmentType.FOLLOW_UP,
  "devchk-ofc": AppointmentType.FOLLOW_UP,
  "devchkrm": AppointmentType.FOLLOW_UP,
  "devicechk": AppointmentType.FOLLOW_UP,
  "return": AppointmentType.FOLLOW_UP,
  "rpm": AppointmentType.FOLLOW_UP,
  "web": AppointmentType.UNKNOWN,
  "tcm": AppointmentType.FOLLOW_UP,
  "pcm": AppointmentType.FOLLOW_UP,
  "advcrpln": AppointmentType.FOLLOW_UP,
  "new pt": AppointmentType.NEW_PATIENT_CHECKIN,
  "vascnewpt": AppointmentType.NEW_PATIENT_CHECKIN,
  "veinnewasg": AppointmentType.NEW_PATIENT_CHECKIN,
  "veinnewun": AppointmentType.NEW_PATIENT_CHECKIN,
  "epnewestpt": AppointmentType.NEW_PATIENT_CHECKIN,
  "iccinlvst": AppointmentType.NEW_PATIENT_CHECKIN,
  "est pt": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "vascestpt": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "veinestpt": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "eov incto": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "nurse": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "consult": AppointmentType.SPECIALIST_CONSULT,
  "hf clinic": AppointmentType.SPECIALIST_CONSULT,
  "strctvalcl": AppointmentType.SPECIALIST_CONSULT,
  "valve": AppointmentType.SPECIALIST_CONSULT,
  "pilotexam": AppointmentType.SPECIALIST_CONSULT,
  "lsm-cons": AppointmentType.SPECIALIST_CONSULT,
  "telehealth": AppointmentType.TELEHEALTH,
  "healowtv": AppointmentType.TELEHEALTH,
  "th incto": AppointmentType.TELEHEALTH,
  "tel": AppointmentType.UNKNOWN,
  "medwellav": AppointmentType.ROUTINE_CHECKUP,
  "imphyexam": AppointmentType.ROUTINE_CHECKUP,
  "bp check": AppointmentType.ROUTINE_CHECKUP,
  "fluvacc": AppointmentType.ROUTINE_CHECKUP,
  "resnonbill": AppointmentType.RESEARCH,
  "researchpt": AppointmentType.RESEARCH,
  "resbill": AppointmentType.RESEARCH,
  "research": AppointmentType.RESEARCH,
  "abdaofull": AppointmentType.PROCEDURE,
  "abddupaoat": AppointmentType.PROCEDURE,
  "abddupvivc": AppointmentType.PROCEDURE,
  "abdominal": AppointmentType.PROCEDURE,
  "abiresting": AppointmentType.PROCEDURE,
  "abiseg": AppointmentType.PROCEDURE,
  "abiw/dup": AppointmentType.PROCEDURE,
  "abiw/exer": AppointmentType.PROCEDURE,
  "ablation": AppointmentType.PROCEDURE,
  "arterial": AppointmentType.PROCEDURE,
  "ascproc120": AppointmentType.PROCEDURE,
  "ascproc30": AppointmentType.PROCEDURE,
  "ascproc60": AppointmentType.PROCEDURE,
  "ascproc90": AppointmentType.PROCEDURE,
  "ascproc": AppointmentType.PROCEDURE,
  "ca/smadupl": AppointmentType.PROCEDURE,
  "carotidbil": AppointmentType.PROCEDURE,
  "carotiduni": AppointmentType.PROCEDURE,
  "carotid": AppointmentType.PROCEDURE,
  "ccohlabih": AppointmentType.PROCEDURE,
  "comstock": AppointmentType.PROCEDURE,
  "ctcalcium": AppointmentType.PROCEDURE,
  "cta": AppointmentType.PROCEDURE,
  "ct": AppointmentType.PROCEDURE,
  "cv": AppointmentType.PROCEDURE,
  "dialaccdpx": AppointmentType.PROCEDURE,
  "dispensev": AppointmentType.PROCEDURE,
  "echoavopt": AppointmentType.PROCEDURE,
  "echobubble": AppointmentType.PROCEDURE,
  "echodef": AppointmentType.PROCEDURE,
  "echolimtd": AppointmentType.PROCEDURE,
  "echostrain": AppointmentType.PROCEDURE,
  "echostrhrt": AppointmentType.PROCEDURE,
  "echo": AppointmentType.PROCEDURE,
  "ekg": AppointmentType.PROCEDURE,
  "ett": AppointmentType.PROCEDURE,
  "event": AppointmentType.PROCEDURE,
  "evlt": AppointmentType.PROCEDURE,
  "extctpulse": AppointmentType.PROCEDURE,
  "extmonitor": AppointmentType.PROCEDURE,
  "fdg": AppointmentType.PROCEDURE,
  "fillpoint": AppointmentType.PROCEDURE,
  "handrejuvn": AppointmentType.PROCEDURE,
  "holter": AppointmentType.PROCEDURE,
  "hosp h-p": AppointmentType.PROCEDURE,
  "hosp proc": AppointmentType.PROCEDURE,
  "hosproc120": AppointmentType.PROCEDURE,
  "hosproc30": AppointmentType.PROCEDURE,
  "hosproc60": AppointmentType.PROCEDURE,
  "hosproc90": AppointmentType.PROCEDURE,
  "hst": AppointmentType.PROCEDURE,
  "iv study": AppointmentType.PROCEDURE,
  "jhs": AppointmentType.PROCEDURE,
  "lab": AppointmentType.PROCEDURE,
  "le venuni": AppointmentType.PROCEDURE,
  "leartbiabi": AppointmentType.PROCEDURE,
  "leartbil": AppointmentType.PROCEDURE,
  "leartpsudo": AppointmentType.PROCEDURE,
  "leartunabi": AppointmentType.PROCEDURE,
  "leartuni": AppointmentType.PROCEDURE,
  "lesegpress": AppointmentType.PROCEDURE,
  "levenbilat": AppointmentType.PROCEDURE,
  "loop rec": AppointmentType.PROCEDURE,
  "lsm-echo": AppointmentType.PROCEDURE,
  "lsm-tread": AppointmentType.PROCEDURE,
  "lux": AppointmentType.PROCEDURE,
  "murj": AppointmentType.PROCEDURE,
  "nuclear": AppointmentType.PROCEDURE,
  "nuclexi": AppointmentType.PROCEDURE,
  "nucmuga": AppointmentType.PROCEDURE,
  "nuctread": AppointmentType.PROCEDURE,
  "obldx": AppointmentType.PROCEDURE,
  "oblinterv": AppointmentType.PROCEDURE,
  "pacemaker": AppointmentType.PROCEDURE,
  "pet/ct": AppointmentType.PROCEDURE,
  "petscan": AppointmentType.PROCEDURE,
  "pet": AppointmentType.PROCEDURE,
  "pft": AppointmentType.PROCEDURE,
  "phlebectom": AppointmentType.PROCEDURE,
  "pmd": AppointmentType.PROCEDURE,
  "pta": AppointmentType.PROCEDURE,
  "pyp": AppointmentType.PROCEDURE,
  "recora": AppointmentType.PROCEDURE,
  "renalus": AppointmentType.PROCEDURE,
  "renal": AppointmentType.PROCEDURE,
  "rhythmexp": AppointmentType.PROCEDURE,
  "rnalkiddup": AppointmentType.PROCEDURE,
  "rnalkiddult": AppointmentType.PROCEDURE,
  "rfablation": AppointmentType.PROCEDURE,
  "rxadmin": AppointmentType.PROCEDURE,
  "scl": AppointmentType.PROCEDURE,
  "sleep": AppointmentType.PROCEDURE,
  "spect": AppointmentType.PROCEDURE,
  "strechquad": AppointmentType.PROCEDURE,
  "stressecho": AppointmentType.PROCEDURE,
  "stresstest": AppointmentType.PROCEDURE,
  "stress": AppointmentType.PROCEDURE,
  "tbi": AppointmentType.PROCEDURE,
  "tilttable": AppointmentType.PROCEDURE,
  "treadmill": AppointmentType.PROCEDURE,
  "ueartbil": AppointmentType.PROCEDURE,
  "ueartuni": AppointmentType.PROCEDURE,
  "uesegpress": AppointmentType.PROCEDURE,
  "uevenbil": AppointmentType.PROCEDURE,
  "uevenuni": AppointmentType.PROCEDURE,
  "usguidescl": AppointmentType.PROCEDURE,
  "varithena": AppointmentType.PROCEDURE,
  "vasclrproc": AppointmentType.PROCEDURE,
  "veinmaplng": AppointmentType.PROCEDURE,
  "veinmapsrt": AppointmentType.PROCEDURE,
  "veinproc": AppointmentType.PROCEDURE,
  "venaseal": AppointmentType.PROCEDURE,
  "veninsfbil": AppointmentType.PROCEDURE,
  "veninsfuni": AppointmentType.PROCEDURE,
  "venousdpt": AppointmentType.PROCEDURE,
  "venous": AppointmentType.PROCEDURE,
  "viablstudy": AppointmentType.PROCEDURE,
  "x-ray": AppointmentType.PROCEDURE,
  "abbott": AppointmentType.PROCEDURE,
  "biotronic": AppointmentType.PROCEDURE,
  "lhc w/pci": AppointmentType.PROCEDURE,
  "medtronic": AppointmentType.PROCEDURE,
  "hvcal": AppointmentType.PROCEDURE,
  "hvpsp": AppointmentType.PROCEDURE,
  "vein": AppointmentType.PROCEDURE,
};

export function getHeartplaceScheduleConfigPath(): string {
  return "configuration/groups/heartplace/rules/schedule.yaml";
}

export const HEARTPLACE_USER_ID: string[] = [
  "83b44f2e-3b3a-475e-8379-2025ffe0b21a",
  "52c0ec34-fcc8-40ab-bb7e-d0345b98b245",
  "1a1ba903-4220-4486-b790-89b5d7c46d6f",
  "66caaddf-6f0a-4e8c-ab5d-2f12f1a0a0b6",
  "6dda182e-0c5a-44b1-ac0f-69c778d01b6f",
  "35518859-82e8-4493-8a3b-ff04855b7b29",
  "25de1696-6ed4-483b-87da-a78003a2c8d6",
  "72d5dfaf-3460-4819-9277-b9750d956180",
  "3b96e84e-631c-4770-bf80-48434d8c1926",
  "ca1b4f7c-fa50-465a-baf1-9cfb426f958b",
  "4b54fd02-d047-41dc-953c-2205f659a139",
  "cf1de675-8fee-40b2-8bf9-fa87b7a6ab4a",
  "678fd1c2-c2fb-4828-a79e-053c2daa7222",
  "b4296bb7-12fd-4a02-81e3-444f3fefd395",
  "a4d6eb9d-e9fb-4cb5-9c3d-58ad9550e25d",
  "dbec6bbf-e6af-4057-bb7d-608ab6bc579f",
  "250a5fb7-63ff-4838-8463-83c50afbd922",
  "0fd268a7-6678-476e-af23-3fb2b3e7ac93",
  "0601993b-6b7b-46a2-a391-c5e66fb2387c",
  "af7e6ea7-0a8a-4f7d-a0d7-559be9ebd413",
];

export const HEARTPLACE_ECW_CREDENTIALS: EcwCredentials = {
  ecwLoginUrl: "https://login.microsoftonline.com/32b3f666-e840-492a-abab-fe1e75b52b93/saml2",
  ecwLoginType: "ecw_tfa",
  ecwUserIds: HEARTPLACE_USER_ID,
};

export const HEARTPLACE_AUTO_INTAKE_RULES: AutoIntakeRule[] = [
  autoIntakeRule(Classification.ADMIN_DISABILITY_FORMS, 0.0),
  autoIntakeRule(Classification.CLEARANCE_REQUEST, 0.9),
  autoIntakeRule(Classification.IMAGING_RESULTS_CT, 0.95),
  autoIntakeRule(Classification.IMAGING_RESULTS_ECHO, 0.95),
  autoIntakeRule(Classification.IMAGING_RESULTS_EKG, 0.95),
  autoIntakeRule(Classification.IMAGING_RESULTS_ENDOSCOPY, 0.9),
  autoIntakeRule(Classification.IMAGING_RESULTS_ULTRASOUND, 0.95),
  autoIntakeRule(Classification.IMAGING_RESULTS_X_RAY, 0.9),
  autoIntakeRule(Classification.LAB_RESULTS),
  autoIntakeRule(Classification.LAB_RESULTS_BP, 0.9),
  autoIntakeRule(Classification.LAB_RESULTS_PT_INR, 0.95),
  autoIntakeRule(Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC, 0.9),
  autoIntakeRule(Classification.MEDICAL_RECORD_REQUEST, 0.9),
  autoIntakeRule(Classification.PHARMACY_RELATED_CARE_CONSIDERATION, 0.95),
  autoIntakeRule(Classification.PHARMACY_RELATED_CLARIFICATION, 0.95),
  autoIntakeRule(Classification.PHARMACY_RELATED_REFILL, 0.95),
  autoIntakeRule(Classification.PHARMACY_RELATED_ALERT, 0.9),
  autoIntakeRule(Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST, 0.95),
  autoIntakeRule(Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE, 0.95),
  autoIntakeRule(Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE, 0.95),
  autoIntakeRule(Classification.PROCEDURE_CARDIAC, 0.9),
  autoIntakeRule(Classification.PROGRESS_NOTE, 0.95),
  autoIntakeRule(Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY, 0.9),
  autoIntakeRule(Classification.PROGRESS_NOTE_OPERATIVE_REPORT, 0.95),
  autoIntakeRule(Classification.REFERRAL, 0.25),
  autoIntakeRule(Classification.REFERRAL_RESPONSE, 0.9),
];

export const HEARTPLACE_AUTO_INTAKE_ELIGIBILITY_REQUIREMENTS: AutoIntakeEligibilityRequirements = [
  AutoIntakeEligibilityRequirement.MATCHED_PROVIDER_NPI,
  AutoIntakeEligibilityRequirement.GEOCODED_LOCATION,
  AutoIntakeEligibilityRequirement.PATIENT_PHONE_NUMBER,
];

const HEARTPLACE_AUTOBOOK_TARGETS: string[] = [
  // Madiha Khan
  "1134651169",
];

export function isProviderEnabledForAutobookHeartplace(receivingProvider: Practitioner): boolean {
  if (!receivingProvider) return false;

  const npi = receivingProvider.npiNumber?.trim();

  if (npi) {
    return HEARTPLACE_AUTOBOOK_TARGETS.includes(npi);
  }

  // No NPI to match against isn't an autobook target — fall back to normal outreach like every other
  // gate in this feature. Throwing here would propagate out of tryAutobook and fail the Cloud Task, so
  // the patient would get neither an autobooking nor the fallback outreach.
  logger.warn("No NPI found for receiving provider when autobooking; falling back to outreach", {
    receivingProvider,
  });

  return false;
}
